import { chatKindOf } from '@bt-common/schemas/wa/chat-kind';
import { TaskColumn } from "@common-models/task-column";
import { Campaign } from "@common-models/campaign-model";
import { UserInterface } from "@common-models/user";
import { createDeepEqualSelector } from "@common-services/deep-equal-selector";
import { createSelector } from "@reduxjs/toolkit";

import { isScheduleValid } from "@common-services/utils";
import { isMessageOverdue } from "@/bt-common/services/utilsCommon";

import { MessageLog, MessageStatusList, MessageStatus } from "@common/models/message-log";
import { CommonRootState } from "@common/types/common-root-state-type";
import { UserMessage } from "@bt-common/schemas/bt/user-message";
import { isNotOnWhatsAppFailure } from "@common-components/message-status/message-status-config";
import { statusRank, logToAxes, maxReceipt, deriveMessageStatus, type DerivedMessageStatus, type StatusAxes, type ReceiptState, type WireState } from "@bt-common/services/message-status";
import { boardsSelectByQuery, campaignsSelectByQuery, campaignsSelectOneObjectByQuery, isTempId, messageLogSelectByQuery, taskColumnSelectByQuery, tasksSelectByQueryOnlyLength, userMessagesSelectByQuery, userMessagesSelectOneObjectByQuery, campaignLogSelectByQuery } from "@common-reducers/DBServiceThunks";
import { activeSubscriptionSelector, selectUser } from "./UserSelectors";
import { selectMergedContactMap } from "./WhatsAppReducer";
import { MessageLogUpdateService } from "@common/services/message-log-update-service";
import { CampaignProgressStats, computeCampaignProgress } from "@common/services/campaign-progress";
import moment from "moment";
import { fetchRule } from "@common-components/schedule-picker/recurrence-utils";
import { calculateNextDuedate } from "@/bt-common/utils/recurrence-calculations";
import { tzidOfRuleString } from "@common-services/utils/tzid-rule-frame";

type MessageLogStatus = typeof MessageStatusList[number];

/**
 * Group logs by time proximity - logs within thresholdMs of each other are grouped together
 * @param sortedLogs - Logs already sorted chronologically (oldest first)
 * @param thresholdMs - Time threshold in milliseconds (default: 10 seconds)
 * @returns Array of log groups, where each group contains logs within the time threshold
 */
function groupLogsByTimeProximity(logs: MessageLog[], thresholdMs: number = 10000): MessageLog[][] {
    if (logs.length === 0) {
        return [];
    }

    const groups: MessageLog[][] = [];
    let currentGroup: MessageLog[] = [logs[0]];
    // Track the last-in-group timestamp numerically so we only allocate one moment per log
    let lastTimeInGroup = moment(logs[0].createdAt).valueOf();

    for (let i = 1; i < logs.length; i++) {
        const currentLog = logs[i];

        const currentTime = moment(currentLog.createdAt).valueOf();
        const timeDifference = currentTime - lastTimeInGroup;

        if (timeDifference <= thresholdMs) {
            // Within threshold - add to current group
            currentGroup.push(currentLog);
        } else {
            // Exceeds threshold - start a new group
            groups.push(currentGroup);
            currentGroup = [currentLog];
        }

        // The current log is now the last log in the current group either way
        lastTimeInGroup = currentTime;
    }

    // Don't forget the last group
    if (currentGroup.length > 0) {
        groups.push(currentGroup);
    }

    return groups;
}

export const getUserIdIfExistForBulkDashboard = createSelector(
    [
        (state: CommonRootState) => state
    ],
    (state: CommonRootState) => {
        const user = selectUser as UserInterface

        return user._id;
    }
);


export const getIfSubscriptionHasBulk = createSelector(
    [
        (state: CommonRootState) => state
    ],
    (state: CommonRootState) => {
        const activeSubscription = activeSubscriptionSelector(state)

        return activeSubscription?.hasBulk;
    }
);


export const getSelectedListId = createSelector(
    [
        (state: CommonRootState) => state
    ],
    (state: CommonRootState) => {
        const { contactListPanelState: contactListPanel } = state.CampaignReducer;

        return contactListPanel?.selectedListId;
    }
);


export const getSelectedTemplateId = createSelector(
    [
        (state: CommonRootState) => state
    ],
    (_state: CommonRootState): string | undefined => {
        // templatePanelState was removed from CampaignDashboardState; this selector
        // is orphan (no live callers) but kept for export-surface compatibility.
        return undefined;
    }
);


export const getTemplatePlaceholderListForCampaign = createSelector(
    [
        (state: CommonRootState) => state.CampaignReducer.newCampaign,
        (state: CommonRootState) => state.DBTaskColumnReducer,
    ],
    (newCampaign, taskColumnReducer) => {
        const boardIDArray = newCampaign?.boardIDArray

        let columnList: TaskColumn[] = []
        for (let boardId of boardIDArray ?? []) {
            const currentBoardIdColumnList = taskColumnSelectByQuery(
                taskColumnReducer,
                {
                    $or: [{ deleted: { $exists: false } }, { deleted: false }],
                    boardId,
                },
                ["type", "title"],
                {
                    order: 1,
                }
            )
                .filter(column => ['number', 'link', 'text-cell', 'phone-cell', 'option-picker', 'complexity-picker',
                    'status-option-picker', 'datepicker',].includes(column.type)) // in case not all column types are supported
            columnList = [...columnList, ...currentBoardIdColumnList]
        }
        const seen = new Set();
        let returnList: string[] = columnList
            .map((column: TaskColumn) => column.title ?? column.type)
            .filter(str => {
                const normalizedStr = str.replace(/\s+/g, '').toLowerCase();
                if (seen.has(normalizedStr)) {
                    return false;
                }
                seen.add(normalizedStr);
                return true;
            })

        returnList.unshift('Whatsapp name')

        return returnList
    }
);



/**
 * Per-board task count, used by:
 *   - the audience-row count chip,
 *   - getIsCampaignWithoutContacts (Next button gate on the audiences tab).
 *
 * Count source: the LIVE loaded contacts when this board's tasks are actually in
 * the store (`loaded > 0`), else the server-denormalized `board.tasksCount`.
 *
 * Why live-first: `board.tasksCount` can go STALE after an edit — the server bumps
 * it, but that refresh reaches the client via MQTT / a re-fetch that is
 * version-guarded, so a board whose version didn't change keeps its OLD count.
 * In stores where that happens (notably the EXTENSION on web.whatsapp.com) a
 * `tasksCount`-first read MASKS a just-made add/remove, so the "N contacts" chip
 * never moves until a full reload — while the website (where the field is often
 * absent) updated live off the loaded tasks. Preferring the loaded count when
 * contacts are present makes add/remove reflect immediately in BOTH stores.
 *
 * `tasksCount` stays the fallback for boards whose tasks aren't loaded — the
 * management list never fetches per-board contacts, and a freshly-created board's
 * denormalization may lag — so we don't regress those to a bogus 0.
 */
export const getTaskSumByBoardIdRecord = createSelector(
    [
        (state: CommonRootState) => state.DBBoardsReducer,
        (state: CommonRootState) => state.DBTasksReducer,
    ],
    (boardsReducer, tasksReducer) => {
        const boards = boardsSelectByQuery(
            boardsReducer,
            {
                $or: [{ deleted: { $exists: false } }, { deleted: false }],
                type: 'audience',
            },
            ["_id", "tasksCount"],
            { order: 1 }
        );

        const tasksRecord: Record<string, number> = {};
        for (const board of boards) {
            if (!board._id) continue;
            // FE#287: `{deleted:false}` is an EXACT sift match and MISSES a fresh
            // contact whose `deleted` field is absent (the common case for a
            // just-added contact), so a 1-contact audience counted 0 and fell back
            // to a stale `tasksCount`. `{$ne:true}` counts undefined + false and
            // excludes only `deleted:true` — matching every other query in the app.
            const loaded = tasksSelectByQueryOnlyLength(tasksReducer, { boardId: board._id, deleted: { $ne: true } });
            tasksRecord[board._id] = loaded > 0 ? loaded : (board.tasksCount ?? 0);
        }
        return tasksRecord;
    }
);


export const getBoardNameByBoardIdRecord = createSelector(
    [
        (state: CommonRootState) => state.DBBoardsReducer,
    ],
    (boardsReducer) => {
        const boards = boardsSelectByQuery(
            boardsReducer,
            {
                $or: [{ deleted: { $exists: false } }, { deleted: false }],
                type: 'audience',
            },
            ["_id", "name"],
            {
                order: 1,
            }
        )

        let namesRecord: Record<string, string | undefined> = {}

        for (let board of boards) {
            if (!board._id) continue;
            namesRecord = {
                ...namesRecord,
                [board._id]: board.name
            }
        }

        return namesRecord
    }
);


export const getCampaignSelectedAudenicesAmount = createSelector([(state: CommonRootState) => state],
    (state: CommonRootState) => {
        const newCampaign = state.CampaignReducer.newCampaign

        return (newCampaign?.boardIDArray?.length ?? 0)
    });


export const getCampaignDueDate = createSelector([(state: CommonRootState) => state], (state: CommonRootState) => {
    const newCampaign = state.CampaignReducer.newCampaign
    return newCampaign?.schedule?.dueDate
});


export const getIsCampaginMessageExist = createSelector([
    (state: CommonRootState) => state.CampaignReducer.activeTab,
    (state: CommonRootState) => state.CampaignReducer.newCampaign,
    (state: CommonRootState) => state.CampaignReducer.currentTemplateDraft
], (activeTab, newCampaign, currentTemplateDraft) => {
    // Use currentTemplateDraft when on messageAndFiles tab (user is actively editing)
    // Use newCampaign.template for other tabs (after sync has happened)
    const currentTemplate = activeTab === 'messageAndFiles' 
        ? currentTemplateDraft 
        : newCampaign?.template;
        
    const isCampaginMessageExist = currentTemplate?.message ||
        currentTemplate?.linkPreview?.url ||
        currentTemplate?.linkPreview?.title ||
        currentTemplate?.linkPreview?.description

    return isCampaginMessageExist
}
);



export const getIsAudienceRowChecked = createSelector(
    [
        (state: CommonRootState, itemId) => state,
        (state: CommonRootState, itemId) => itemId,
    ],
    (state: CommonRootState, itemId) => {
        // const checked = newCampaign?.boardIDArray?.includes(itemId)
        const newCampaign = state.CampaignReducer.newCampaign

        return (newCampaign?.boardIDArray ?? []).includes(itemId)
    }
);


export const getIsCampaignWithoutContacts = createSelector(
    [
        (state: CommonRootState) => state,
    ],
    (state: CommonRootState) => {
        const taskSumByBoardIdRecord = getTaskSumByBoardIdRecord(state)
        const newCampaign = state.CampaignReducer.newCampaign

        let counter = 0
        for (let boardId of (newCampaign?.boardIDArray ?? [])) {
            if (boardId) {
                counter += taskSumByBoardIdRecord[boardId] ?? 0
            }
        }

        return counter === 0
    }
);


export const getFilteredIdArray = createSelector(
    [
        (state: CommonRootState, searchTerm) => state,
        (state: CommonRootState, searchTerm) => searchTerm,
    ],
    (state: CommonRootState, searchTerm) => {
        const mergedContactMap = selectMergedContactMap(state);
        const campaignIdForLogsView = state.CampaignReducer.campaignIdForLogsView
        const filteredKeys = Object.keys(mergedContactMap)
            .filter(key => mergedContactMap[key]?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()))

        const messageLogs = messageLogSelectByQuery(
            state.DBMessageLogReducer,
            {
                campaignId: campaignIdForLogsView,
            },
            [],
            {
                order: 1,
            }
        )

        const filteredMessageLogs = messageLogs.filter(log => log?.message.contactList?.[0]?.id &&
            filteredKeys.includes(log?.message.contactList?.[0]?.id))
            .map(log => log._id)

        return filteredMessageLogs
    }
);


export const getIsCampaignCompleteAndAllLogsAreComplete = createSelector(
    [
        (state: CommonRootState, campaignId) => state,
        (state: CommonRootState, campaignId) => campaignId,
    ],
    (state: CommonRootState, campaignId) => {
        const campaignLogs = messageLogSelectByQuery(state.DBMessageLogReducer, {
            $or: [
                { deleted: { $exists: false } },
                { deleted: false }
            ],
            campaignId,
        })
        const campaign = campaignsSelectOneObjectByQuery(state.DBCampaignReducer, {
            _id: campaignId,
        })

        const numberOfLogs = campaignLogs?.length
        const numberOfSentLogs = campaignLogs?.filter(log => log.status === 'bt-sent')?.length
        const isCampaignComplete = campaign?.campaignStatus === "completed"

        return isCampaignComplete && (numberOfLogs === numberOfSentLogs)
    }
);

export const selectNumberOfAudienceSelected = createSelector(
    [
        (state: CommonRootState) => state.CampaignReducer.newCampaign
    ],
    (newCampaign) => {
        return newCampaign?.boardIDArray?.length ?? 0;
    }
);

export const selectCanContinueToNextTab = createSelector(
    [
        selectNumberOfAudienceSelected,
        getIsCampaignWithoutContacts,
        (state: CommonRootState) => state.CampaignReducer.activeTab,
        (state: CommonRootState) => state.CampaignReducer.newCampaign,
        (state: CommonRootState) => state.CampaignReducer.currentTemplateDraft,
        (state: CommonRootState) => state.ScheduledMessageEditorReducer.isFileUploading,
    ],
    (isAudienceSelected, isCampaignWithoutContacts, activeTab, newCampaign, currentTemplateDraft, isFileUploading) => {
        // If a file is still uploading, block navigation regardless of tab
        if (isFileUploading) {
            return false;
        }
        // Use currentTemplateDraft when on messageAndFiles tab (user is actively editing)
        // Use newCampaign.template for other tabs (after sync has happened)
        const currentTemplate = activeTab === 'messageAndFiles' 
            ? currentTemplateDraft 
            : newCampaign?.template;

        const isPollValid = () => {

            function haveSimilarOptions() {
                const options = ((currentTemplate?.poll?.options ?? []) as Array<{ text: string }>).filter((option) => option.text !== '');
                const uniqueOptions = [...new Set(options.map((option) => option.text))];
                return options.length !== uniqueOptions.length;
            }


            return (currentTemplate?.isPoll &&
                currentTemplate?.poll?.question?.length > 0 &&
                currentTemplate?.poll?.options?.length > 1 &&
                currentTemplate?.poll?.options?.filter((o: { text: string }) => o.text.length > 0).length > 1 &&
                !haveSimilarOptions())
        }

        const isMessageExist = !currentTemplate?.isPoll && (
            currentTemplate?.message ||
            currentTemplate?.linkPreview?.url ||
            currentTemplate?.linkPreview?.title ||
            currentTemplate?.linkPreview?.description ||
            currentTemplate?.asset?.type?.includes('audio') ||
            currentTemplate?.asset?.url
        )

        const isCampaignMessageExist = isMessageExist || isPollValid()

        const isCampaignDueDateExist = !!newCampaign?.schedule?.dueDate


        let canContinueToNextTab = false;

        switch (activeTab) {
            case "audiences":
                canContinueToNextTab = !!isAudienceSelected && !isCampaignWithoutContacts;
                break;
            case "messageAndFiles":
                canContinueToNextTab = !!isCampaignMessageExist;
                break;
            case "timeAndDate":
                canContinueToNextTab = isCampaignDueDateExist;
                break;
            default:
                break;
        }

        return canContinueToNextTab;
    }
);

export const selectIsTimingValid = createSelector([(state: CommonRootState) => state.CampaignReducer.newCampaign],
    (newCampaign) => {
        const isNow = newCampaign?.schedule?.isNow;

        return isNow || isScheduleValid(newCampaign?.schedule?.dueDate);
    }
);


export const getRunningCampaigns = createSelector(
    [(state: CommonRootState) => state.DBCampaignReducer],
    (DBCampaignReducer) => {
        // `complete-sent` is intentionally NOT here: all-sent = done (see getCampaignRealStatus /
        // campaign-progress). It must not linger in the running set waiting for delivery acks.
        const candidateCampaigns = campaignsSelectByQuery(DBCampaignReducer, {
            campaignStatus: { $in: ['running', 'pending'] },
            contactCount: { $exists: true }
        }, [], { createdAt: -1 })

        const nowUtc = moment.utc();

        const runningCampaigns = candidateCampaigns.filter(campaign => {
            if (campaign.campaignStatus === 'running') return true;
            if (campaign.campaignStatus === 'pending') {
                const dueDate = campaign.schedule?.dueDate;
                return !!dueDate && moment.utc(dueDate).isBefore(nowUtc);
            }
            return false;
        })

        return runningCampaigns;
    }
);

export const isAnyCampaignInProgressSelector = createSelector(
    [getRunningCampaigns],
    (getRunningCampaigns) => {

        const isAnyCampaignInProgressSelectorRes = getRunningCampaigns.length > 0
        return isAnyCampaignInProgressSelectorRes
    }
);

// getRelevantMessageLogsByCampaignId as a selector
export const getRelevantMessageLogsByCampaignId = createSelector(
    [(state: CommonRootState) => state.DBMessageLogReducer, (_, campaignId) => campaignId],
    (DBMessageLogReducer, campaignId) => {
        const campaignSortedFilteredMessageLogs = messageLogSelectByQuery(
            DBMessageLogReducer,
            {
                campaignId,
                $or: [
                    { waMessageKey: { $exists: true } },
                    { status: "cancelled" }
                ]
            },
            [],  // No projection fields specified; adjust if needed
            { createdAt: -1 }  // Sort by `createdAt` in descending order
        );

        const groupedLogs = campaignSortedFilteredMessageLogs.reduce<Record<string, MessageLog>>((uniqueLogsByContactId, log) => {
            const contactId = getContactIdFromLog(log);
            if (!contactId) {
                // Skip logs without contact IDs
                return uniqueLogsByContactId;
            }

            // Store the log if it's the first one for this contact or if it has waMessageKey while the existing entry does not
            if (!uniqueLogsByContactId[contactId] || uniqueLogsByContactId[contactId].waMessageKey === undefined) {
                uniqueLogsByContactId[contactId] = log;
            }

            return uniqueLogsByContactId;
        }, {});

        // Convert the object to an array of logs
        return Object.values(groupedLogs ?? []) as MessageLog[];
    }
);

// getStuckMessagesByCampaignId as a selector
export const getStuckMessagesByCampaignId = createSelector(
    [(state: CommonRootState) => state.DBUserMessageReducer, (_, campaignId) => campaignId], (DBUserMessageReducer, campaignId) => {
        const campaignPendingMessages = userMessagesSelectByQuery(DBUserMessageReducer, { campaignId }, [], { createdAt: -1 });

        return campaignPendingMessages.filter((message) => isMessageOverdue(message));
    }
);

// getRunningCampaignsWithLogsAndMessages as a selector
export const getRunningCampaignsWithLogsAndMessages = createSelector(
    [
        getRunningCampaigns,
        (state: CommonRootState) => state.DBMessageLogReducer,
        (state: CommonRootState) => state.DBUserMessageReducer,
    ],
    (runningCampaigns, messageLogReducer, userMessageReducer) => {
        // Reconstruct a minimal state for the memoized per-campaign helpers below. Their input
        // selectors only read these two slices, so the memo keys are identical to a full-state call.
        const slicedState = {
            DBMessageLogReducer: messageLogReducer,
            DBUserMessageReducer: userMessageReducer,
        } as CommonRootState;
        return runningCampaigns.map((campaign) => {
            const campaignMessageLogs = getRelevantMessageLogsByCampaignId(slicedState, campaign._id);
            const stuckMessages = getStuckMessagesByCampaignId(slicedState, campaign._id);

            return {
                ...campaign,
                campaignMessageLogs,
                stuckMessages
            };
        });
    }
);

export const dateOfReference = new Date('2024-11-06T11:10:21.508271');

export const selectCampaignNumberOfContactsById = createDeepEqualSelector(
    [
        (state: CommonRootState, campaignId) => state.DBBoardsReducer,
        (state: CommonRootState, campaignId) => state,
        (state: CommonRootState, campaignId) => campaignId
    ],
    (DBBoardsReducer, state, campaignId) => {
        const campaign = campaignsSelectOneObjectByQuery(state.DBCampaignReducer, {
            _id: campaignId,
        })
        if (campaign?.contactCount) return campaign.contactCount
        else {
            const taskSumByBoardIdRecord = getTaskSumByBoardIdRecord(state)


            let counter = 0
            for (let boardId of (campaign?.boardIDArray ?? [])) {
                if (boardId) {
                    counter += taskSumByBoardIdRecord[boardId] ?? 0
                }
            }

            return counter
        }
    }
);

export const selectCampaignPendingMessageCountByCampaignId = createDeepEqualSelector(
    [
        (state: CommonRootState, campaignId) => campaignId,
        (state: CommonRootState, campaignId) => state,
    ],
    (campaignId, state) => {
        const pendingMessages = userMessagesSelectByQuery(state.DBUserMessageReducer, {
            campaignId,
            status: 'pending'
        })
        return pendingMessages.length
    }
)

export const selectCampaignNumberOfContactsByCampaignId = createDeepEqualSelector(
    [
        (state: CommonRootState, campaignId) => state.DBBoardsReducer,
        (state: CommonRootState, campaignId) => campaignId,
        (state: CommonRootState, campaignId) => state,
    ],
    (DBBoardsReducer, campaignId, state) => {
        const campaign = campaignsSelectOneObjectByQuery(state.DBCampaignReducer, { _id: campaignId });
        const taskSumByBoardIdRecord = getTaskSumByBoardIdRecord(state)
        let counter = 0
        for (let boardId of (campaign?.boardIDArray ?? [])) {
            if (boardId) {
                counter += taskSumByBoardIdRecord[boardId] ?? 0
            }
        }

        return counter
    }
);

export const getCampaignsWithPendingMessagesIds = createDeepEqualSelector(
    [
        (state: CommonRootState) => state,
    ],
    (state: CommonRootState) => {
        const pendingMessages = userMessagesSelectByQuery(state.DBUserMessageReducer, {
            status: 'pending'
        });
        // create a unique list of all the message._id in messages
        const pendingMessagesCampaignIds = pendingMessages.filter(message => message.campaignId).map(message => message.campaignId);
        const uniqueCampaignIds = [...new Set(pendingMessagesCampaignIds)];

        return uniqueCampaignIds;
    }
);

export const selectCampaignLogs = createSelector(
    [
        (state: CommonRootState) => state.DBMessageLogReducer,
        (state: CommonRootState, campaignId) => campaignId
    ],
    (messageLogState, campaignId) => {
        return messageLogSelectByQuery(messageLogState, {
            campaignId
        }, [], {
            createdAt: -1
        });
    }
);


/**
 * Helper function to get contact ID(s) from a message log
 * Returns concatenated contact IDs with hyphens if multiple contacts exist
 */
function getContactIdFromLog(log: any): string | null {
    const contactList = log?.message?.contactList;
    if (!contactList || contactList.length === 0) {
        return null;
    }
    // Map to IDs and join with hyphens
    return contactList.map((contact: any) => contact.id).filter(Boolean).join('-');
}

/**
 * Selector to get contact ID from a message log
 * Returns concatenated contact IDs with hyphens if multiple contacts exist
 */
export const selectContactIdFromMessageLog = createSelector(
    [
        (_state: CommonRootState, messageLog: any) => messageLog
    ],
    (messageLog): string | null => {
        return getContactIdFromLog(messageLog);
    }
);

/**
 * Reusable selector to get unique message logs per contact (most recent per contact)
 * Used by multiple selectors to avoid code duplication
 * Groups by contact ID instead of messageId - concatenates multiple contact IDs with hyphens
 */
export const selectUniqueMessageLogsByCampaign = createDeepEqualSelector(
    [
        (state: CommonRootState, campaignId: string) => state.DBMessageLogReducer,
        (state: CommonRootState, campaignId: string) => campaignId
    ],
    (messageLogState, campaignId) => {
        const uniqueMessageLogs: Record<string, any> = {};
        // Numeric timestamp of the currently-kept log per contact — avoids re-allocating moments per comparison
        const uniqueMessageLogTimes: Record<string, number> = {};

        Object.values(messageLogState.entities)
            .filter((log): log is NonNullable<typeof log> => log !== undefined && log.campaignId === campaignId)
            .forEach(current => {
                const contactId = getContactIdFromLog(current);
                if (!contactId) {
                    // Skip logs without contact IDs
                    return;
                }

                const currentTime = moment(current.createdAt).valueOf();
                const existingLog = uniqueMessageLogs[contactId];

                if (!existingLog) {
                    uniqueMessageLogs[contactId] = current;
                    uniqueMessageLogTimes[contactId] = currentTime;
                    return;
                }

                // Prioritize based on the shared wire-axis rank (bt-common statusRank;
                // lower = more advanced). Legacy receipt-status rows floor to the
                // unknown rung so a `confirmed` row wins over a stale `read`/`played`.
                const currentLevel = statusRank(current.status);
                const existingLevel = statusRank(existingLog.status);

                if (currentLevel < existingLevel) {
                    uniqueMessageLogs[contactId] = current;
                    uniqueMessageLogTimes[contactId] = currentTime;
                } else if (currentLevel === existingLevel) {
                    // If levels are equal, pick the latest one
                    if (currentTime > uniqueMessageLogTimes[contactId]) {
                        uniqueMessageLogs[contactId] = current;
                        uniqueMessageLogTimes[contactId] = currentTime;
                    }
                }
            });

        return uniqueMessageLogs;
    }
);

// selectRecentCampaignLogsPerMessageId - now uses the reusable selector
export const selectRecentCampaignLogsPerMessageId = createDeepEqualSelector(
    [
        (state: CommonRootState, campaignId) => campaignId,
        (state: CommonRootState, campaignId) => state,
    ],
    (campaignId, state) => {
        const uniqueMessageLogs = selectUniqueMessageLogsByCampaign(state, campaignId);
        return Object.values(uniqueMessageLogs);
    }
);

export const getNumberOfSentWithoutAck = createDeepEqualSelector(
    [
        (state: CommonRootState, campaignId) => state,
        (state: CommonRootState, campaignId) => campaignId,
    ],
    (state: CommonRootState, campaignId) => {
        const sentMessageLog = messageLogSelectByQuery(state.DBMessageLogReducer, { campaignId, status: 'bt-sent' })

        const sentMessageLogWithAckMap = sentMessageLog.reduce((map, message) => {
            if (message.ack && message.messageId) {
                map[message.messageId] = message;
            }
            return map;
        }, {} as Record<string, any>);


        const sentMessageLogWithoutAckOrDuplicate = sentMessageLog.filter(message => {
            return !message.ack && message.messageId !== undefined && !sentMessageLogWithAckMap[message.messageId];
        })
        return sentMessageLogWithoutAckOrDuplicate?.length ?? 0
    }
);

export const getNumberOfPendingWithoutAck = createDeepEqualSelector(
    [
        (state: CommonRootState, campaignId) => state,
        (state: CommonRootState, campaignId) => campaignId,
    ],
    (state: CommonRootState, campaignId) => {
        const pendingMessageLog = messageLogSelectByQuery(state.DBMessageLogReducer, { campaignId, status: 'pending' })

        const pendingMessageLogWithoutAckMap = pendingMessageLog.reduce((map, message) => {
            if (message.ack && message.messageId) {
                map[message.messageId] = message;
            }
            return map;
        }, {} as Record<string, any>);

        const pendingMessageLogWithoutAckOrDuplicate = pendingMessageLog.filter(message => {
            return !message.ack && message.messageId !== undefined && !pendingMessageLogWithoutAckMap[message.messageId];
        })
        return pendingMessageLogWithoutAckOrDuplicate?.length ?? 0
    }
);

export const getNumberOfErrorWithoutAck = createDeepEqualSelector(
    [
        (state: CommonRootState, campaignId) => state,
        (state: CommonRootState, campaignId) => campaignId,
    ],
    (state: CommonRootState, campaignId) => {
        const errorMessageLog = messageLogSelectByQuery(state.DBMessageLogReducer, { campaignId, status: 'failed' })
        const errorMessageLogWithoutAckMap = errorMessageLog.reduce((map, message) => {
            if (message.ack && message.messageId) {
                map[message.messageId] = message;
            }
            return map;
        }, {} as Record<string, any>);

        const errorMessageLogWithoutAckOrDuplicate = errorMessageLog.filter(message => {
            return !message.ack && message.messageId !== undefined && !errorMessageLogWithoutAckMap[message.messageId];
        })
        return errorMessageLogWithoutAckOrDuplicate?.length ?? 0
    }

);



const getScheduledMessages = createSelector(
    [(state: CommonRootState) => state.DBUserMessageReducer],
    (userMessageReducer) => {
        const scheduledMessages = Object.values(userMessageReducer.entities).filter(
            (message): message is NonNullable<typeof message> => message !== undefined && !isTempId(message._id) && !message.deleted
        );
        return scheduledMessages ?? []
    }
);


const getStuckMessages = createSelector(
    [(state: CommonRootState) => getScheduledMessages(state)],
    (userMessageList) => {

        let userMessageListFiltered: typeof userMessageList = []

        for (const message of userMessageList) {
            if (!message) continue;

            const isPending = message.status === "pending" || message.status === undefined;
            const isOverDue = isMessageOverdue(message)

            if (isPending && isOverDue) {
                userMessageListFiltered.push(message)
            }
        }

        return userMessageListFiltered
    }
);

export const selectFilteredCampaignLogCampaignIds = createSelector(
    [
        (state: CommonRootState) => state.DBCampaignLogReducer,
        (_state: CommonRootState, userId: string) => userId,
        (_state: CommonRootState, _userId: string, searchQuery: string) => searchQuery,
        (_state: CommonRootState, _userId: string, _searchQuery: string, statusFilter: string) => statusFilter,
        (_state: CommonRootState, _userId: string, _searchQuery: string, _statusFilter: string, sourceFilter: string) => sourceFilter,
        (_state: CommonRootState, _userId: string, _searchQuery: string, _statusFilter: string, _sourceFilter: string, clientTypeFilter: string) => clientTypeFilter,
    ],
    (campaignLogState, userId, searchQuery, statusFilter, sourceFilter, clientTypeFilter) => {
        if (!userId) return [];

        const logs = campaignLogSelectByQuery(campaignLogState as any, {
            owner: userId,
        }, [], { createdAt: -1 });

        // Filter logs
        const filteredLogs = logs.filter(log => {
            // Status filter
            if (statusFilter !== 'all' && log.status !== statusFilter) {
                return false;
            }

            // Source filter
            if (sourceFilter !== 'all' && log.source !== sourceFilter) {
                return false;
            }

            // Client type filter
            if (clientTypeFilter !== 'all' && log.clientType !== clientTypeFilter) {
                return false;
            }

            // Search query
            if (searchQuery) {
                const queryLower = searchQuery.toLowerCase();
                const logMessage = log.metadata?.logMessage?.toLowerCase() || '';
                const status = log.status?.toLowerCase() || '';
                const campaignId = log.campaignId ? String(log.campaignId).toLowerCase() : '';
                return logMessage.includes(queryLower) || status.includes(queryLower) || campaignId.includes(queryLower);
            }

            return true;
        });

        // Group by campaignId and sort by latest log
        const campaignGroups = new Map<string, number>(); // campaignId -> latest timestamp
        
        filteredLogs.forEach(log => {
            if (log.campaignId) {
                const campaignIdStr = String(log.campaignId);
                const currentLatest = campaignGroups.get(campaignIdStr) || 0;
                const logTime = moment(log.createdAt).valueOf();
                if (logTime > currentLatest) {
                    campaignGroups.set(campaignIdStr, logTime);
                }
            }
        });

        // Return sorted campaignIds
        return Array.from(campaignGroups.entries())
            .sort((a, b) => b[1] - a[1])
            .map(([campaignId]) => campaignId);
    }
);

export const selectCampaignLogGroupsByCampaignId = createSelector(
    [
        (state: CommonRootState) => state.DBCampaignLogReducer,
        (state: CommonRootState, campaignId: string) => campaignId
    ],
    (campaignLogState, campaignId) => {
        return campaignLogSelectByQuery(campaignLogState as any, {
            campaignId: campaignId,
        }, [], { createdAt: -1 });
    }
);

export const selectCampaignDetails = createSelector(
    [
        (state: CommonRootState) => state.DBCampaignReducer,
        (state: CommonRootState, campaignId: string) => campaignId
    ],
    (campaignReducer, campaignId) => {
        return campaignsSelectOneObjectByQuery(campaignReducer, { _id: campaignId });
    }
);

export const getMessagesByCampaignId = createSelector(
    [
        (state: CommonRootState, _campaignId: string | undefined) => state.DBUserMessageReducer,
        (_state: CommonRootState, campaignId: string | undefined) =>  campaignId
    ],
        (userMessageReducer, campaignId) => {
        const messageList = userMessagesSelectByQuery(userMessageReducer, {
            $or: [
              { deleted: { $exists: false } },
              { deleted: false }
            ],
            campaignId,
            
          }, null, { createdAt: -1 })
            return messageList
    }
);

export const getCampaignStuckMessages = createSelector(
    [getStuckMessages, (_, campaignId) => campaignId],
    (stuckMessages, campaignId) => {
        return stuckMessages.filter(message => message.campaignId === campaignId);
    }
);

export interface AggregatedMessageLog {
    messageId: string;
    latestStatus: MessageLogStatus;
    logCount: number;
    latestUpdate: string;
    logs: any[];
}

export interface MessageLogRow {
    id: string;
    messageId: string;
    message: any;  // Original message object
    latestStatus: MessageLogStatus;
    hasError?: boolean;  // Indicates if any log has an error (for error indicator icon)
    logCount: number | null;
    latestUpdate: string;
    isMainRow: boolean;
    dueDate?: string | Date;
    logs?: MessageLog[][];  // Array of log groups, where each group contains logs within 10 seconds of each other
}

export const selectAllMessageLogRows = createSelector(
    [
        (state: CommonRootState) => state.DBMessageLogReducer,
        (state: CommonRootState, userId: string) => userId,
        (state: CommonRootState, userId: string, expandedRows: { [key: string]: boolean }) => expandedRows,
    ],
    (messageLogState, userId, expandedRows): MessageLogRow[] => {
        // Fetch ALL logs for this user (no filtering by status, attemptType, etc.)
        const logs = messageLogSelectByQuery(messageLogState, {
            owner: userId || '',
            $or: [
                { deleted: { $exists: false } },
                { deleted: false }
            ]
        }, [], { 
            createdAt: -1
        });
        
        // Debug: Log total logs found
        if (process.env.NODE_ENV === 'development' && logs.length > 0) {
            console.log(`[selectAllMessageLogRows] Found ${logs.length} total logs for userId: ${userId}`);
            // Count logs by messageId to see distribution
            const logCountsByMessageId = logs.reduce((acc, log) => {
                if (log.messageId) acc[log.messageId] = (acc[log.messageId] || 0) + 1;
                return acc;
            }, {} as Record<string, number>);
            console.log('[selectAllMessageLogRows] Logs per messageId:', logCountsByMessageId);
        }

        // First aggregate the logs by messageId
        const logsByMessageId = logs.reduce((acc: { [key: string]: MessageLog[] }, log) => {
            // Ensure log has messageId
            if (!log.messageId) {
                console.warn('[selectAllMessageLogRows] Log missing messageId:', log);
                return acc;
            }
            
            if (!acc[log.messageId]) {
                acc[log.messageId] = [];
            }
            acc[log.messageId].push(log);
            return acc;
        }, {});

        // Then determine latest status for each messageId
        const aggregatedLogs = Object.entries(logsByMessageId).map(([messageId, messageLogs]) => {
            // Sort logs chronologically (newest first) to find the actual latest log
            const sortedLogs = [...messageLogs].sort((a, b) => 
                moment(b.createdAt).valueOf() - moment(a.createdAt).valueOf()
            );
            
                // Get the latest log (first after sorting newest first)
                const latestLog = sortedLogs[0];
                
                // Use the actual status from the latest log (don't override to 'error')
                // Errors are indicated separately with an icon indicator
                const latestStatus = latestLog.status as MessageLogStatus;
                
                // Check if any log has an error for error indicator
                const hasError = messageLogs.some(log => log.error || log.message?.status === 'failed');
                
                return {
                    messageId,
                    latestStatus: latestStatus,
                    hasError: hasError, // Add flag to indicate if errors exist
                    logCount: messageLogs.length,
                    latestUpdate: latestLog.createdAt,
                    logs: messageLogs // Keep unsorted for now, will sort chronologically later for timeline
                };
        });
        
        // Debug: Log aggregation results for troubleshooting
        if (process.env.NODE_ENV === 'development') {
            aggregatedLogs.forEach(agg => {
                // Find latest log for debug
                const sortedForDebug = [...agg.logs].sort((a, b) => 
                    moment(b.createdAt).valueOf() - moment(a.createdAt).valueOf()
                );
                const latestLogForDebug = sortedForDebug[0];
                console.log(`[selectAllMessageLogRows] MessageId: ${agg.messageId}, LatestStatus: ${agg.latestStatus}, LatestLog:`, {
                    _id: latestLogForDebug._id,
                    status: latestLogForDebug.status,
                    createdAt: latestLogForDebug.createdAt,
                    logMessage: latestLogForDebug.metadata?.logMessage
                }, `Total Logs: ${agg.logCount}`);
            });
        }

        // Then transform into rows with expansion logic
        return aggregatedLogs
            .sort((a, b) => moment(b.latestUpdate).valueOf() - moment(a.latestUpdate).valueOf())
            .flatMap(log => {
                // Find the latest log to get the message content
                const latestLog = log.logs.reduce((latest, current) => {
                    const currentTime = moment(current.createdAt);
                    const latestTime = moment(latest.createdAt);
                    return currentTime.isAfter(latestTime) ? current : latest;
                }, log.logs[0]);

                // Sort logs chronologically (oldest first) for timeline display
                // If timestamps are identical (same millisecond), sort by status (worse statuses first)
                // This ensures progression order: sent → delivered → read → played
                const sortedLogs = [...log.logs].sort((a, b) => {
                    const timeA = moment(a.createdAt).valueOf();
                    const timeB = moment(b.createdAt).valueOf();
                    
                    // Primary sort: by timestamp
                    if (timeA !== timeB) {
                        return timeA - timeB;
                    }
                    
                    // Secondary sort (only when timestamps are identical): by wire-axis
                    // rank (reverse) — higher rank (less advanced) first, keeping the
                    // natural progression order in the timeline.
                    const statusLevelA = statusRank(a.status);
                    const statusLevelB = statusRank(b.status);
                    return statusLevelB - statusLevelA;
                });

                // Group sorted logs by time proximity (10 second threshold)
                const groupedLogs = groupLogsByTimeProximity(sortedLogs, 10000);

                const mainRow: MessageLogRow = {
                    id: log.messageId ?? '',
                    messageId: log.messageId ?? '',
                    message: latestLog.message,
                    latestStatus: log.latestStatus as MessageLogStatus,
                    hasError: log.hasError, // Pass through error flag
                    logCount: log.logCount,
                    latestUpdate: log.latestUpdate ?? '',
                    isMainRow: true,
                    dueDate: latestLog.message.dueDate,
                    logs: groupedLogs  // Array of groups (array of arrays)
                };

                if (expandedRows[log.messageId]) {
                    // For backwards compatibility, keep sub-rows but they're less needed now
                    // Timeline view will use mainRow.logs instead
                    // Flatten grouped logs and sort descending (newest first) with secondary sort by status
                    const allLogs = (groupedLogs || []).flat(); // Flatten groups to flat array
                    const reverseSortedLogs = [...allLogs].sort((a, b) => {
                        const timeA = moment(a.createdAt).valueOf();
                        const timeB = moment(b.createdAt).valueOf();
                        
                        // Primary sort: by timestamp (descending)
                        if (timeA !== timeB) {
                            return timeB - timeA;
                        }
                        
                        // Secondary sort (only when timestamps are identical): by wire-axis
                        // rank — lower rank = more advanced, so better statuses come first
                        // when descending.
                        const statusLevelA = statusRank(a.status);
                        const statusLevelB = statusRank(b.status);
                        return statusLevelA - statusLevelB;
                    });
                    
                    const subRows: MessageLogRow[] = reverseSortedLogs.map((subLog, index) => ({
                        id: `${log.messageId ?? ''}-${index}`,
                        messageId: '↳ ' + moment(subLog.createdAt).format('h:mm:ss A'),
                        message: subLog.message,
                        latestStatus: subLog.status as MessageLogStatus,
                        logCount: null,
                        latestUpdate: subLog.createdAt ?? '',
                        isMainRow: false,
                        dueDate: subLog.message?.dueDate
                    }));
                    return [mainRow, ...subRows];
                }
                return [mainRow];
            });
    }
);

/**
 * Selector to get unique messageIds from message logs for a user
 * Used in MessageLogsTab to get the list of messages to display
 */
export const selectMessageLogMessageIds = createSelector(
    [
        (state: CommonRootState) => state.DBMessageLogReducer,
        (state: CommonRootState, userId: string) => userId,
    ],
    (messageLogState, userId): string[] => {
        const logs = messageLogSelectByQuery(messageLogState, {
            owner: userId || '',
            $or: [
                { deleted: { $exists: false } },
                { deleted: false }
            ]
        }, [], { 
            createdAt: -1
        });

        // Get unique messageIds and their latest update time in a single pass
        // (avoids re-filtering all logs and re-allocating moments per messageId)
        const messageIds = new Set<string>();
        const latestUpdateByMessageId = new Map<string, number>();
        logs.forEach(log => {
            if (log.messageId) {
                messageIds.add(log.messageId);
                const logTime = moment(log.createdAt).valueOf();
                const previousLatest = latestUpdateByMessageId.get(log.messageId);
                if (previousLatest === undefined || logTime > previousLatest) {
                    latestUpdateByMessageId.set(log.messageId, logTime);
                }
            }
        });

        // Sort by latest update time (newest first)
        const messageIdsWithLatestTime = Array.from(messageIds).map(messageId => ({
            messageId,
            latestUpdate: latestUpdateByMessageId.get(messageId) as number,
        }));

        return messageIdsWithLatestTime
            .sort((a, b) => b.latestUpdate - a.latestUpdate)
            .map(item => item.messageId);
    }
);

/**
 * Selector to get all messageIds for a contact in a campaign
 * Used to collect all messages for a contact when displaying logs
 */
export const selectMessageIdsByContactAndCampaign = createSelector(
    [
        (state: CommonRootState) => state.DBMessageLogReducer,
        (state: CommonRootState, campaignId: string, contactId: string) => campaignId,
        (state: CommonRootState, campaignId: string, contactId: string) => contactId,
    ],
    (messageLogState, campaignId, contactId): string[] => {
        const logs = messageLogSelectByQuery(messageLogState, {
            campaignId: campaignId,
            $or: [
                { deleted: { $exists: false } },
                { deleted: false }
            ]
        }, [], { 
            createdAt: -1
        });

        // Get unique messageIds for this contact
        const messageIds = new Set<string>();
        logs.forEach(log => {
            const logContactId = getContactIdFromLog(log);
            if (logContactId === contactId && log.messageId) {
                messageIds.add(log.messageId);
            }
        });

        return Array.from(messageIds);
    }
);

/**
 * Selector to get sorted and grouped logs for a specific messageId or multiple messageIds
 * Used in MessageLogCard to display the timeline
 */
export const selectMessageLogGroupsByMessageId = createSelector(
    [
        (state: CommonRootState) => state.DBMessageLogReducer,
        (state: CommonRootState, messageId: string | string[]) => Array.isArray(messageId) ? messageId : [messageId],
    ],
    (messageLogState, messageIds): MessageLog[][] => {
        // If no messageIds provided, return empty array
        if (!messageIds || messageIds.length === 0) {
            return [];
        }

        // Query with $in operator (works fine for single element arrays too)
        const query: any = {
            messageId: { $in: messageIds },
            $or: [
                { deleted: { $exists: false } },
                { deleted: false }
            ]
        };

        const logs = messageLogSelectByQuery(messageLogState, query, [], { 
            createdAt: -1
        });

        if (logs.length === 0) {
            return [];
        }

        // Sort logs chronologically (oldest first) for timeline display
        // If timestamps are identical (same millisecond), sort by status (worse statuses first)
        const sortedLogs = [...logs].sort((a, b) => {
            const timeA = moment(a.createdAt).valueOf();
            const timeB = moment(b.createdAt).valueOf();
            
            // Primary sort: by timestamp
            if (timeA !== timeB) {
                return timeA - timeB;
            }
            
            // Secondary sort (only when timestamps are identical): by wire-axis rank (reverse)
            const statusLevelA = statusRank(a.status);
            const statusLevelB = statusRank(b.status);
            return statusLevelB - statusLevelA;
        });

        // Group sorted logs by time proximity (10 second threshold)
        return groupLogsByTimeProximity(sortedLogs, 10000);
    }
);

/**
 * Selector for filtered messageIds
 * Applies search, status, attempt type, and WhatsApp status filters
 */
export const selectFilteredMessageLogMessageIds = createSelector(
    [
        (state: CommonRootState, userId: string) => selectMessageLogMessageIds(state, userId),
        (state: CommonRootState) => state.DBMessageLogReducer,
        (_state: CommonRootState, userId: string) => userId,
        (_state: CommonRootState, _userId: string, searchQuery: string) => searchQuery,
        (_state: CommonRootState, _userId: string, _searchQuery: string, statusFilter: MessageStatus | 'all') => statusFilter,
        (_state: CommonRootState, _userId: string, _searchQuery: string, _statusFilter: MessageStatus | 'all', attemptTypeFilter: 'initial' | 'client-retry' | 'scheduler-retry' | 'all') => attemptTypeFilter,
        (_state: CommonRootState, _userId: string, _searchQuery: string, _statusFilter: MessageStatus | 'all', _attemptTypeFilter: 'initial' | 'client-retry' | 'scheduler-retry' | 'all', whatsappStatusFilter: string | 'all') => whatsappStatusFilter,
    ],
    (messageIds, messageLogState, userId, searchQuery, statusFilter, attemptTypeFilter, whatsappStatusFilter): string[] => {
        // OPTIMIZATION: Get all logs once and group by messageId instead of querying per messageId
        // This reduces complexity from O(n*m) to O(n + m) where n = messageIds, m = total logs
        // Filter by userId to only process logs for this user
        const allLogs = messageLogSelectByQuery(messageLogState, {
            owner: userId || '',
            $or: [
                { deleted: { $exists: false } },
                { deleted: false }
            ]
        }, [], { 
            createdAt: -1
        });
        
        // Group logs by messageId for O(1) lookup.
        // Also track the latest log per messageId in the same pass (numeric timestamps,
        // instead of a moment-allocating reduce per messageId in the filter below).
        const logsByMessageId = new Map<string, typeof allLogs>();
        const latestLogByMessageId = new Map<string, typeof allLogs[number]>();
        const latestLogTimeByMessageId = new Map<string, number>();
        allLogs.forEach(log => {
            if (log.messageId) {
                if (!logsByMessageId.has(log.messageId)) {
                    logsByMessageId.set(log.messageId, []);
                }
                logsByMessageId.get(log.messageId)!.push(log);

                const logTime = moment(log.createdAt).valueOf();
                const previousTime = latestLogTimeByMessageId.get(log.messageId);
                if (previousTime === undefined || logTime > previousTime) {
                    latestLogTimeByMessageId.set(log.messageId, logTime);
                    latestLogByMessageId.set(log.messageId, log);
                }
            }
        });
        
        const queryLower = searchQuery ? searchQuery.toLowerCase() : '';
        const whatsappStatusFilterLower = whatsappStatusFilter !== 'all' ? whatsappStatusFilter.toLowerCase() : null;
        
        return messageIds.filter((messageId) => {
            const logs = logsByMessageId.get(messageId);
            
            if (!logs || logs.length === 0) {
                return false;
            }

            // Get latest log for message content and status (precomputed in the grouping pass above)
            const latestLog = latestLogByMessageId.get(messageId) ?? logs[0];

            const message = latestLog.message;
            const latestStatus = latestLog.status as MessageStatus;

            // Search filter
            if (queryLower) {
                const matchesMessage = message?.message?.toLowerCase().includes(queryLower);
                const matchesContact = message?.contactList?.[0]?.id?.toLowerCase().includes(queryLower) ||
                                      message?.contactList?.[0]?.name?.toLowerCase().includes(queryLower);
                const messageIdStr = messageId ? String(messageId) : '';
                const matchesMessageId = messageIdStr.toLowerCase().includes(queryLower);
                const campaignIdStr = message?.campaignId ? String(message.campaignId) : '';
                const matchesCampaignId = campaignIdStr.toLowerCase().includes(queryLower);
                
                if (!matchesMessage && !matchesContact && !matchesMessageId && !matchesCampaignId) {
                    return false;
                }
            }

            // Status filter
            if (statusFilter !== 'all') {
                if (statusFilter === 'failed') {
                    const hasError = latestStatus === 'failed' ||
                                   logs.some(log => log.error || log.message?.status === 'failed');
                    if (!hasError) {
                        return false;
                    }
                } else if (latestStatus !== statusFilter) {
                    return false;
                }
            }

            // Attempt type filter
            if (attemptTypeFilter !== 'all') {
                const hasMatchingAttemptType = logs.some(
                    log => log.metadata?.attemptType === attemptTypeFilter
                );
                if (!hasMatchingAttemptType) {
                    return false;
                }
            }

            // WhatsApp status filter
            if (whatsappStatusFilterLower) {
                const hasMatchingWhatsappStatus = logs.some(
                    log => log.metadata?.whatsappStatus?.toLowerCase() === whatsappStatusFilterLower
                );
                if (!hasMatchingWhatsappStatus) {
                    return false;
                }
            }

            return true;
        });
    }
);

/**
 * Selector to get message details for a specific messageId or multiple messageIds
 * Returns the message object and metadata needed for MessageLogCard
 * When multiple messageIds provided, uses the latest message from all logs
 */
export const selectMessageLogDetailsByMessageId = createSelector(
    [
        (state: CommonRootState) => state.DBMessageLogReducer,
        (state: CommonRootState, messageId: string | string[]) => Array.isArray(messageId) ? messageId : [messageId],
    ],
    (messageLogState, messageIds) => {
        // If no messageIds provided, return null
        if (!messageIds || messageIds.length === 0) {
            return null;
        }

        // Query with $in operator (works fine for single element arrays too)
        const query: any = {
            messageId: { $in: messageIds },
            $or: [
                { deleted: { $exists: false } },
                { deleted: false }
            ]
        };

        const logs = messageLogSelectByQuery(messageLogState, query, [], { 
            createdAt: -1
        });

        if (logs.length === 0) {
            return null;
        }

        // Get latest log for message content and status (across all messageIds)
        const latestLog = logs.reduce((latest, current) => {
            const currentTime = moment(current.createdAt);
            const latestTime = moment(latest.createdAt);
            return currentTime.isAfter(latestTime) ? current : latest;
        }, logs[0]);

        const latestStatus = latestLog.status as MessageLogStatus;
        const hasError = logs.some(log => log.error || log.message?.status === 'failed');

        // For display, use the primary messageId (first one or the one from latest log)
        const primaryMessageId = Array.isArray(messageIds) ? (latestLog.messageId || messageIds[0]) : messageIds;

        return {
            messageId: primaryMessageId,
            messageIds: messageIds, // Store all messageIds for reference
            message: latestLog.message,
            latestStatus,
            hasError,
            logCount: logs.length,
            latestUpdate: latestLog.createdAt,
            dueDate: latestLog.message?.dueDate,
        };
    }
);

/**
 * Selector for recurrence information from a UserMessage object (recurrence text and next due date)
 * Works directly with UserMessage objects, not message logs
 * Uses specific fields for memoization to ensure proper recalculation
 */
export const selectRecurrenceInfoFromMessage = createSelector(
    [
        (_state: CommonRootState, message: UserMessage) => message?.isRecurring,
        (_state: CommonRootState, message: UserMessage) => message?.rruleset,
        (_state: CommonRootState, message: UserMessage) => message?.dueDate,
        (_state: CommonRootState, message: UserMessage) => message,
    ],
    (isRecurring, rruleset, dueDate, message) => {
        if (!message) {
            return null;
        }
        
        if (!isRecurring || !rruleset) {
            return null;
        }

        try {
            const rule = fetchRule(rruleset);
            const recurrenceText = rule.toText();
            
            // Calculate next due date. BE#648 (M3): thread the rule's own embedded
            // TZID so a migrated series' next-send holds its wall-clock across DST
            // (a no-TZID rule passes undefined -> unchanged legacy path).
            const currentDueDate = dueDate ? moment(dueDate) : moment();
            const nextDueDate = calculateNextDuedate(currentDueDate, message, tzidOfRuleString(rruleset));
            
            if (process.env.NODE_ENV === 'development') {
                console.log('[selectRecurrenceInfoFromMessage]', {
                    isRecurring,
                    hasRruleset: !!rruleset,
                    dueDate,
                    recurrenceText,
                    nextDueDate: nextDueDate ? nextDueDate.format('DD/MM/YYYY, h:mm:ss A') : null
                });
            }
            
            return {
                recurrenceText,
                nextDueDate: nextDueDate ? nextDueDate.local() : null
            };
        } catch (error) {
            console.error('Failed to parse recurrence rule:', error, { rruleset, isRecurring, dueDate });
            return null;
        }
    }
);

/**
 * Selector for recurrence information (recurrence text and next due date)
 * Uses selectMessageLogDetailsByMessageId to get message details
 */
export const selectRecurrenceInfoByMessageId = createSelector(
    [
        (state: CommonRootState, messageId: string | string[]) => selectMessageLogDetailsByMessageId(state, messageId),
    ],
    (messageDetails) => {
        if (!messageDetails?.message) {
            return null;
        }

        const message = messageDetails.message;
        
        if (!message.isRecurring || !message.rruleset) {
            return null;
        }

        try {
            const rule = fetchRule(message.rruleset);
            const recurrenceText = rule.toText();
            
            // Calculate next due date. BE#648 (M3): thread the rule's embedded TZID
            // (no-TZID -> undefined -> unchanged legacy path).
            const currentDueDate = message.dueDate ? moment(message.dueDate) : moment();
            const nextDueDate = calculateNextDuedate(currentDueDate, message, tzidOfRuleString(message.rruleset));
            
            return {
                recurrenceText,
                nextDueDate: nextDueDate ? nextDueDate.local() : null
            };
        } catch (error) {
            console.error('Failed to parse recurrence rule:', error);
            return null;
        }
    }
);

/**
 * Selector for filtered user messages
 * Applies search and status filters
 */
export const selectFilteredUserMessages = createSelector(
    [
        (state: CommonRootState, userId: string) => state.DBUserMessageReducer,
        (state: CommonRootState, userId: string) => userId,
        (_state: CommonRootState, _userId: string, searchQuery: string) => searchQuery,
        (_state: CommonRootState, _userId: string, _searchQuery: string, statusFilter: string | 'all') => statusFilter,
    ],
    (messageReducer, userId, searchQuery, statusFilter): UserMessage[] => {
        const query: any = {
            owner: userId,
            $or: [
                { deleted: { $exists: false } },
                { deleted: false }
            ]
        };

        if (statusFilter !== 'all') {
            query.status = statusFilter;
        }

        const messages = userMessagesSelectByQuery(messageReducer, query, [], { 
            dueDate: -1
        });

        if (!searchQuery) {
            return messages;
        }

        const queryLower = searchQuery.toLowerCase();
        return messages.filter((message) => {
            const matchesMessage = message.message?.toLowerCase().includes(queryLower);
            const matchesContact = message.contactList?.some(contact => 
                contact.id?.toLowerCase().includes(queryLower) ||
                contact.name?.toLowerCase().includes(queryLower)
            );
            const matchesMessageId = message._id?.toLowerCase().includes(queryLower);
            const matchesCampaignId = message.campaignId ? String(message.campaignId).toLowerCase().includes(queryLower) : false;
            
            return matchesMessage || matchesContact || matchesMessageId || matchesCampaignId;
        });
    }
);

/**
 * Selector for filtered message log rows
 * Applies search, status, attempt type, and WhatsApp status filters
 */
export const selectFilteredMessageLogRows = createSelector(
    [
        (state: CommonRootState, userId: string) => selectAllMessageLogRows(state, userId, {}),
        (_state: CommonRootState, _userId: string, searchQuery: string) => searchQuery,
        (_state: CommonRootState, _userId: string, _searchQuery: string, statusFilter: MessageStatus | 'all') => statusFilter,
        (_state: CommonRootState, _userId: string, _searchQuery: string, _statusFilter: MessageStatus | 'all', attemptTypeFilter: 'initial' | 'client-retry' | 'scheduler-retry' | 'all') => attemptTypeFilter,
        (_state: CommonRootState, _userId: string, _searchQuery: string, _statusFilter: MessageStatus | 'all', _attemptTypeFilter: 'initial' | 'client-retry' | 'scheduler-retry' | 'all', whatsappStatusFilter: string | 'all') => whatsappStatusFilter,
    ],
    (allRows, searchQuery, statusFilter, attemptTypeFilter, whatsappStatusFilter): MessageLogRow[] => {
        const mainRows = allRows.filter(row => row.isMainRow);
        
        return mainRows.filter((row) => {
            // Search filter
            if (searchQuery) {
                const query = searchQuery.toLowerCase();
                const matchesMessage = row.message?.text?.toLowerCase().includes(query);
                const matchesContact = row.message?.contactList?.[0]?.id?.toLowerCase().includes(query) ||
                                      row.message?.contactList?.[0]?.name?.toLowerCase().includes(query);
                const matchesMessageId = row.messageId?.toLowerCase().includes(query);
                const matchesCampaignId = row.message?.campaignId?.toLowerCase().includes(query);
                
                if (!matchesMessage && !matchesContact && !matchesMessageId && !matchesCampaignId) {
                    return false;
                }
            }

            // Status filter - also check if any log has error for 'error' filter
            if (statusFilter !== 'all') {
                if (statusFilter === 'failed') {
                    // For error filter, check if latestStatus is error OR if any log has an error
                    // Flatten grouped logs for checking
                    const allLogs = row.logs?.flat() || [];
                    const hasError = row.latestStatus === 'failed' ||
                                   allLogs.some(log => log.error || log.message?.status === 'failed');
                    if (!hasError) {
                        return false;
                    }
                } else if (row.latestStatus !== statusFilter) {
                    return false;
                }
            }

            // Attempt type filter - check all logs for this message (flatten groups)
            if (attemptTypeFilter !== 'all' && row.logs) {
                const allLogs = row.logs.flat();
                const hasMatchingAttemptType = allLogs.some(
                    log => log.metadata?.attemptType === attemptTypeFilter
                );
                if (!hasMatchingAttemptType) {
                    return false;
                }
            }

            // WhatsApp status filter - check all logs for this message (flatten groups)
            if (whatsappStatusFilter !== 'all' && row.logs) {
                const allLogs = row.logs.flat();
                const hasMatchingWhatsappStatus = allLogs.some(
                    log => log.metadata?.whatsappStatus?.toLowerCase() === whatsappStatusFilter.toLowerCase()
                );
                if (!hasMatchingWhatsappStatus) {
                    return false;
                }
            }

            return true;
        });
    }
);

/**
 * Status item interface for message status history
 */
export interface StatusItem {
    status: string;
    timestamp?: string | number;
    color: string;
    users?: Array<{
        id: {
            _serialized: string;
        };
        t: number;
    }>;
    order: number;
}

/**
 * Status order constants
 */
export const STATUS_ORDER = {
    Created: 1,
    Delivered: 2,
    Read: 3,
    Played: 3,
} as const;


/**
 * Selector to generate status history for a message log
 */
export const selectMessageStatusHistory = createSelector(
    [
        (state: CommonRootState, messageLog: MessageLog) => messageLog
    ],
    (messageLog): StatusItem[] => {
        if (!messageLog) return [];

        const { ack, ackData, message } = messageLog;
        const history: StatusItem[] = [
            {
                status: 'Created',
                timestamp: message?.createdAt?.toString(),
                color: 'grey',
                order: STATUS_ORDER.Created
            },
        ];

        if (ackData) {
            if (ackData.delivery && Array.isArray(ackData.delivery)) {
                history.push({
                    status: 'Delivered',
                    color: 'info',
                    users: ackData.delivery,
                    order: STATUS_ORDER.Delivered
                });
            }

            // Only show Read if there are read records and no played records
            if (ackData.read?.length && !ackData.played?.length) {
                history.push({
                    status: 'Read',
                    color: 'success',
                    users: ackData.read,
                    order: STATUS_ORDER.Read
                });
            }

            // Only show Interacted if there are played records
            if (ackData.played?.length) {
                history.push({
                    status: 'Interacted',
                    color: 'success',
                    users: ackData.played,
                    order: STATUS_ORDER.Played
                });
            }
        }

        return history.sort((a, b) => a.order - b.order);
    }
);


/**
 * Selector to get message info by waMessageKey
 */
export const selectMessageInfoByKey = createSelector(
    [
        (state: CommonRootState) => state.MessageInfoReducer,
        (state: CommonRootState, waMessageKey: string) => waMessageKey
    ],
    (messageInfoState, waMessageKey) => {
        return messageInfoState?.messageInfoMap[waMessageKey] || null;
    }
);

/**
 * Selector to get message info data (unwrapped) by waMessageKey
 */
export const selectMessageInfoDataByKey = createSelector(
    [
        (state: CommonRootState) => state.MessageInfoReducer,
        (state: CommonRootState, waMessageKey: string) => waMessageKey
    ],
    (messageInfoState, waMessageKey) => {
        const messageInfo = messageInfoState?.messageInfoMap[waMessageKey];
        // If the response has a data wrapper, extract it
        // Use type assertion to check for data property
        return (messageInfo as any)?.data || messageInfo || null;
    }
);

/**
 * Selector to get loading state for message info by waMessageKey
 */
export const selectMessageInfoLoading = createSelector(
    [
        (state: CommonRootState) => state.MessageInfoReducer,
        (state: CommonRootState, waMessageKey: string) => waMessageKey
    ],
    (messageInfoState, waMessageKey) => {
        return messageInfoState?.loadingStates[waMessageKey] || false;
    }
);

/**
 * Selector to get error state for message info by waMessageKey
 */
export const selectMessageInfoError = createSelector(
    [
        (state: CommonRootState) => state.MessageInfoReducer,
        (state: CommonRootState, waMessageKey: string) => waMessageKey
    ],
    (messageInfoState, waMessageKey) => {
        return messageInfoState?.errorStates[waMessageKey] || null;
    }
);

export const selectPaginatedMessageLogRows = createSelector(
    [
        (state: CommonRootState, userId: string, expandedRows: { [key: string]: boolean }) => 
            selectAllMessageLogRows(state, userId, expandedRows),
        (state: CommonRootState, userId: string, expandedRows: { [key: string]: boolean }, page: number) => page,
        (state: CommonRootState, userId: string, expandedRows: { [key: string]: boolean }, page: number, pageSize: number) => pageSize
    ],
    (allRows, page = 0, pageSize = 25): MessageLogRow[] => {
        const start = page * pageSize;
        const end = start + pageSize;
        return allRows.slice(start, end);
    }
);




export const getCampaignLogsAckData = createSelector(
    [
        (state: CommonRootState) => state,
        (_state: CommonRootState, campaignId: string) => campaignId
    ],
    (state, campaignId) => {

        const messageLogs = selectCampaignLogs(state, campaignId);

            const FormattedAckData = messageLogs?.map(log => {
                return {messageId: log.messageId, ackData: log.ackData}
            })
            return FormattedAckData
    }
);

export interface MessageStats {
    sent: number;        // primary 'sent' — reached the wire but NOT yet confirmed by WhatsApp
    confirmed: number;   // primary 'confirmed' — WhatsApp's servers acknowledged it, no receipt yet (1:1 ack≥1 or a confirmed group)
    delivered: number;   // receipt axis: delivered (grey ✓✓)
    read: number;        // receipt axis: read (blue ✓✓)
    played: number;
    failed: number;
    pending: number;
    total: number;
    expired: number; // Messages with status "bt-sent" but past retry limit
}

/**
 * Pick the representative log for one message from all its logs: the one with the
 * most-advanced wire status (lowest statusRank, bt-common). Legacy receipt-derived
 * status strings ('played'/'read'/'delivered') are NOT ranked — they sink to the
 * unknown floor and can never win over a real wire status, which is what protects
 * us from a stale bogus 'played' row (pre-migration, written before ack became the
 * single receipt source of truth). On a rank tie, prefer the log with the higher
 * ack (more-advanced receipt on the same wire row). Shared by computeCampaignAxes
 * (grouped per messageId) and selectCampaignMessageStats (grouped per contact) so
 * the resolution rule lives in exactly one place.
 */
function pickRepresentativeLog<T extends { status?: string; ack?: number }>(logs: T[]): T | null {
    let best: T | null = null;
    let bestRank = Infinity;
    for (const log of logs) {
        if (!log.status) continue;
        const rank = statusRank(log.status);
        if (!best || rank < bestRank) {
            bestRank = rank;
            best = log;
        } else if (rank === bestRank && (log.ack ?? 0) > (best.ack ?? 0)) {
            best = log;
        }
    }
    return best;
}

/**
 * Two-axis projection for a resolved (representative) log, adding the terminal
 * "expired" rule the raw logToAxes has no way to know about: a message that
 * reached the wire ('sent') but is overdue — its retry window is exhausted
 * (isMessageOverdue, the SAME helper + embedded `.message` the per-contact stats
 * have always used) — is classified 'failed'. It went out but we gave up
 * advancing it, so it counts as RESOLVED and stops showing "still sending",
 * mirroring the old `expired` bucket. Anchored on the literal 'sent' status (not
 * the wire axis) so a `confirmed`/`read` message is never demoted by being old.
 * Non-overdue messages project exactly as logToAxes → approved fixture unchanged.
 */
function projectLogToAxes(status: string, ack: number | undefined, isGroup: boolean, embeddedMessage?: UserMessage): StatusAxes {
    if (status === 'sent' && embeddedMessage?.dueDate && isMessageOverdue(embeddedMessage)) {
        return { wire: 'failed', receipt: 'none' };
    }
    return logToAxes(status, ack, isGroup);
}

/**
 * BE#658: does the userMessage itself prove delivery to WhatsApp? Strict, self-contained
 * predicate (deliberately NOT getEffectiveMessageStatus): delivery is proven ONLY by
 *   - status === 'confirmed', or
 *   - a `failed`/`expired` doc carrying a REAL WhatsApp ack (ack >= 1 or serverAck >= 1) —
 *     the dual-engine race / stale-reaper case (FE#249/#141).
 * It does NOT count `acknowledged === true`: that is the admin Messages-Dashboard "Zero Inbox"
 * triage dismissal, not a WhatsApp ack (counting it masked a GENUINE failure as delivered —
 * green badge, no error icon, no Retry). It also does not count a handed-off send with a key
 * but ack 0 (ack-gate 5.8.32: ack < 1 never reached the server). Used to override a stuck/stray
 * campaign LOG trail that would otherwise read failed/queued.
 */
export function messageHasDeliveryEvidence(message: UserMessage | undefined | null): boolean {
    if (!message) return false;
    if (message.status === 'confirmed') return true;
    if (message.status === 'failed' || message.status === 'expired') {
        const ack = (message as { ack?: number }).ack;
        const serverAck = (message as { serverAck?: number }).serverAck;
        return (typeof ack === 'number' && ack >= 1) || (typeof serverAck === 'number' && serverAck >= 1);
    }
    return false;
}

/**
 * BE#658: is a message "confirmed or beyond" — i.e. it reached WhatsApp and must NOT offer
 * Retry? Pure decision shared by selectIsConfirmedOrBeyond (which feeds selectShouldShowRetry).
 * Delivery evidence on the message wins FIRST: a delivered send (confirmed userMessage, or a
 * `failed`/`expired` doc carrying a real waMessageKey/ack) is confirmed-or-beyond even when its
 * messagelog trail's representative row is a stray `failed`/`before-wa-send` — otherwise the
 * log-trail-only check wrongly offered Retry on an already-delivered message (duplicate-send
 * risk). With no delivery evidence, fall back to the representative log status folded by the
 * canonical reader.
 */
export function isConfirmedOrBeyondFrom(
    mostAdvancedStatus: string | null | undefined,
    deliveryEvidenceMessage: UserMessage | undefined | null,
): boolean {
    if (messageHasDeliveryEvidence(deliveryEvidenceMessage)) return true;
    if (!mostAdvancedStatus) return false;
    return deriveMessageStatus(mostAdvancedStatus, undefined, false).primary === 'confirmed';
}

/**
 * Per-message badge status: resolve one message's many logs to the canonical
 * two-axis {primary, receipt} the badge renders. Mirrors computeCampaignAxes for a
 * single message:
 *  1. pick the representative row via pickRepresentativeLog (statusRank — same rule
 *     the aggregate stats use, so badge and meter never disagree on the rep row);
 *  2. fold an overdue/retry-exhausted 'sent' rep into 'expired' → primary 'failed'
 *     (the client-side "past retry limit" state);
 *  3. aggregate the receipt across ALL rows with maxReceipt — legacy receipt info
 *     can live on a sibling row (e.g. a `read` row next to the `confirmed` row that
 *     won the wire pick), so the badge recovers "Confirmed · Read";
 *  4. a positive receipt (delivered/read) is hard delivery evidence → force
 *     `confirmed` even if the rep row is terminally 'failed' (dup-send edge), and
 *     never surface a receipt on a failed/queued primary.
 * Returns null when there is no usable log.
 */
export function deriveMessageBadgeStatus(
    logs: { status?: string; ack?: number }[],
    isGroup: boolean,
    overdueMessage?: UserMessage,
): DerivedMessageStatus | null {
    const rep = pickRepresentativeLog(logs);
    if (!rep || !rep.status) return null;

    const repStatus = (rep.status === 'sent' && overdueMessage?.dueDate && isMessageOverdue(overdueMessage))
        ? 'expired'
        : rep.status;

    const aggregatedReceipt = logs.reduce<ReceiptState>(
        (acc, row) => (row.status ? maxReceipt(acc, logToAxes(row.status, row.ack, isGroup).receipt) : acc),
        'none',
    );
    const hasPositiveReceipt = aggregatedReceipt === 'delivered' || aggregatedReceipt === 'read';

    let { primary } = deriveMessageStatus(repStatus, rep.ack, isGroup);
    if (hasPositiveReceipt) primary = 'confirmed';
    // BE#658: the userMessage is the source of truth for delivery. If it carries delivery
    // evidence (getEffectiveMessageStatus → 'confirmed': confirmed status, or a `failed`/
    // `expired` doc with a real waMessageKey/ack — the dual-engine display race), a stuck or
    // stray log trail (only `before-wa-send` from a dropped confirmed-log write, or a
    // losing-engine/timeout `failed` row) must not paint the delivered send red/queued.
    // Mirrors the primary badge's FE#249 override; never promotes a genuinely-failed send
    // (no delivery evidence → stays 'failed').
    else if ((primary === 'failed' || primary === 'queued') && messageHasDeliveryEvidence(overdueMessage)) {
        primary = 'confirmed';
    }

    const receipt: ReceiptState = (primary === 'confirmed' || primary === 'sent') ? aggregatedReceipt : 'none';
    return { primary, receipt };
}

/**
 * Pure core of selectCampaignMessageStats — the per-contact bucketing, extracted
 * so it is directly unit-testable with plain log rows (no reducer/selector plumbing)
 * and provably cross-checkable against the reconciled computeCampaignAxes. Behavior
 * is IDENTICAL to the inline body it replaced.
 *
 * Groups the already-gathered logs by contact id (getContactIdFromLog — the
 * concatenated contactList ids), resolves each contact's representative row via the
 * shared pickRepresentativeLog rule, then buckets into the MessageStats shape. The
 * overdue-'sent'→'expired' special-case keeps its OWN bucket here (unlike
 * computeCampaignAxes, which folds it into wire:'failed'); everything else projects
 * through logToAxes. `total` comes from campaign.contactCount.
 */
export function computeCampaignMessageStats(
    allLogs: MessageLog[],
    campaign?: { contactCount?: number },
    // BE#658 M1: the REAL affected shape is a `before-wa-send`-only log trail whose embedded
    // `.message` snapshot was taken BEFORE the send (pending, no key) — so delivery evidence is
    // NOT in the log. Resolve the live store userMessage by id (same source the row badge reads)
    // so the override fires on the real shape, not just the seeded one.
    resolveUserMessage?: (messageId: string) => UserMessage | undefined,
): MessageStats {
    // Group logs by contact ID (concatenated with hyphens if multiple contacts)
    const logsByContactId: Record<string, MessageLog[]> = {};
    allLogs.forEach(log => {
        const contactId = getContactIdFromLog(log);
        if (!contactId) return; // Skip logs without contact IDs
        if (!logsByContactId[contactId]) {
            logsByContactId[contactId] = [];
        }
        logsByContactId[contactId].push(log);
    });

    // Get unique contact IDs
    const contactIds = Object.keys(logsByContactId);

    // Calculate stats using most advanced status for each contact
    const result = contactIds.reduce((acc, contactId) => {
        const logs = logsByContactId[contactId];

        // Shared resolution rule (see pickRepresentativeLog): most-advanced wire
        // status wins, ties broken by higher ack; legacy receipt-status rows can
        // never win. One copy, used here and by computeCampaignAxes.
        const representativeLog = pickRepresentativeLog(logs);

        if (!representativeLog) {
            // If no status found, skip this contact
            return acc;
        }

        const mostAdvancedStatus = representativeLog.status;
        const embeddedMessage = representativeLog.message as UserMessage | undefined;

        // Expired: a 'sent' message past its retry window. MessageStats keeps this
        // as its OWN bucket (the detail views count expired separately from failed),
        // unlike the aggregate {wire,receipt} shape where projectLogToAxes folds it
        // into 'failed'. Same trigger (isMessageOverdue on the embedded message).
        if (mostAdvancedStatus === 'sent' && embeddedMessage?.dueDate && isMessageOverdue(embeddedMessage)) {
            acc.expired++;
            return acc;
        }

        // Canonical two-axis reader (bt-common): primary rung + receipt axis, both
        // from the persisted status + ack integer — never from the legacy ackData
        // blob. `confirmed` is now its OWN bucket (was collapsed into `sent`): a
        // message WhatsApp acknowledged with no delivery receipt yet (1:1 ack≥1, or
        // a confirmed group whose receipt is not_reported) counts as confirmed, not
        // sent. delivered/read still come from the receipt axis.
        const chatId = getContactIdFromLog(representativeLog) ?? undefined;
        const isGroup = chatKindOf(chatId) === 'group';
        const { primary, receipt } = deriveMessageStatus(mostAdvancedStatus, representativeLog.ack, isGroup);

        // BE#658: a delivered userMessage must not be counted failed/pending because its log
        // trail is stuck/stray — same override as the badge. Prefer the live store userMessage
        // (real BE#658 shape: before-wa-send-only trail → embedded snapshot is pre-send), fall
        // back to the embedded snapshot when no resolver/userMessage is available.
        const storeMessage = resolveUserMessage && representativeLog.messageId
            ? resolveUserMessage(representativeLog.messageId)
            : undefined;
        const deliveryMessage = storeMessage ?? embeddedMessage;
        const effectivePrimary =
            (primary === 'failed' || primary === 'queued') && messageHasDeliveryEvidence(deliveryMessage)
                ? 'confirmed'
                : primary;

        const uiCategory: keyof typeof acc =
            effectivePrimary === 'queued' ? 'pending' :
            effectivePrimary === 'failed' ? 'failed' :
            receipt === 'delivered' ? 'delivered' :
            receipt === 'read' ? 'read' :
            effectivePrimary === 'confirmed' ? 'confirmed' : // confirmed, receipt 'none'/'not_reported'
            'sent'; // primary 'sent' — reached the wire, not yet confirmed

        // Increment the appropriate counter based on UI category
        acc[uiCategory]++;

        return acc;
    }, {
        sent: 0,
        confirmed: 0,
        delivered: 0,
        read: 0,
        played: 0,
        failed: 0,
        pending: 0,
        total: campaign?.contactCount || 0,
        expired: 0
    });

    return result;
}

export const selectCampaignMessageStats = createDeepEqualSelector(
    [
        (state: CommonRootState, campaignId: string) => state.DBMessageLogReducer,
        (state: CommonRootState, campaignId: string) => state.DBCampaignReducer,
        (state: CommonRootState, campaignId: string) => state.DBUserMessageReducer,
        (state: CommonRootState, campaignId: string) => state.CampaignReducer.deliveryEvidence,
        (state: CommonRootState, campaignId: string) => campaignId
    ],
    (messageLogReducer, campaignReducer, userMessageReducer, deliveryEvidence, campaignId): MessageStats => {
        const campaign = campaignsSelectOneObjectByQuery(campaignReducer, {
            _id: campaignId,
        });

        // Get all message logs for this campaign
        const allLogs = messageLogSelectByQuery(messageLogReducer, {
            campaignId: campaignId
        });

        // BE#658 M1/4a: resolve delivery evidence by messageId — prefer a full store userMessage,
        // fall back to the dedicated delivery-evidence map (fetched scoped for the log view, since
        // the real affected shape is a before-wa-send-only trail whose embedded snapshot is pre-send).
        const resolveUserMessage = (messageId: string): UserMessage | undefined =>
            (userMessagesSelectOneObjectByQuery(userMessageReducer, { _id: messageId }) as UserMessage | undefined)
            ?? (deliveryEvidence?.[messageId] as UserMessage | undefined)
            ?? undefined;

        return computeCampaignMessageStats(allLogs, campaign, resolveUserMessage);
    }
);

// ─── per-campaign latest-status counts (the drip status meter) ───

/**
 * Latest-status counts for a campaign, conforming to the MessageStatus enum with
 * ack folded in as a secondary receipt sub-state. Every targeted recipient lands
 * in EXACTLY ONE of sending / confirmed / failed; `read` is an independent
 * SECONDARY subset of `confirmed` (all-participants-read — often never reached in
 * big groups, so NOT first-class). See classifyStatusCount for the rules.
 */
export interface CampaignStatusCounts {
    entered: number;    // recipients targeted (campaign.contactCount)
    sending: number;    // dispatched, not yet confirmed (sent/sent_pending_ack/bt-sent/before-wa-send/pending)
    confirmed: number;  // reached WhatsApp's servers: status 'confirmed', OR ack>=1, OR a legacy receipt status
    read: number;       // all participants read (ack>=3 or read/played) — SECONDARY subset of confirmed
    failed: number;     // failed/error/cancelled/expired (incl. an overdue/retry-exhausted 'sent')
    total: number;      // == entered
}

type StatusCountBucket = 'sending' | 'confirmed' | 'failed';

/**
 * Classify one representative log into exactly one primary bucket, plus the
 * secondary `read` flag. Order matters — status FIRST, then ack (the product
 * owner's exact rule):
 *   1. terminal failure (failed/error/cancelled/expired) → failed;
 *   2. an overdue 'sent' whose retry window is exhausted gave up → failed
 *      (mirrors projectLogToAxes / the MessageStats `expired` fold);
 *   3. confirmed = reached WhatsApp: status 'confirmed', OR any ack>=1, OR a
 *      legacy receipt status (delivered/received/read/played);
 *   4. anything else non-terminal is still dispatching → sending.
 * `read` (ack>=3 or read/played) is only meaningful ON a confirmed message, so it
 * is reported solely for the confirmed bucket — a big-group send that never gets
 * per-member read receipts stays confirmed with read:false.
 */
export function classifyStatusCount(
    status: string,
    ack: number | undefined,
    embeddedMessage?: UserMessage,
): { bucket: StatusCountBucket; read: boolean } {
    const a = ack ?? 0;

    if (status === 'failed' || status === 'error' || status === 'cancelled' || status === 'expired') {
        return { bucket: 'failed', read: false };
    }
    if (status === 'sent' && embeddedMessage?.dueDate && isMessageOverdue(embeddedMessage)) {
        return { bucket: 'failed', read: false };
    }

    const legacyReceipt = status === 'delivered' || status === 'received' || status === 'read' || status === 'played';
    if (status === 'confirmed' || a >= 1 || legacyReceipt) {
        const read = a >= 3 || status === 'read' || status === 'played';
        return { bucket: 'confirmed', read };
    }

    return { bucket: 'sending', read: false };
}

/**
 * Pure core of selectCampaignStatusCounts — mirrors computeCampaignMessageStats:
 * groups the campaign's logs by contact id (getContactIdFromLog), resolves each
 * contact's representative row with the shared pickRepresentativeLog rule, then
 * classifies it via classifyStatusCount into the sending/confirmed/failed +
 * secondary-read shape. `entered`/`total` come from campaign.contactCount.
 */
export function computeCampaignStatusCounts(
    allLogs: MessageLog[],
    campaign?: { contactCount?: number },
): CampaignStatusCounts {
    const logsByContactId: Record<string, MessageLog[]> = {};
    allLogs.forEach(log => {
        const contactId = getContactIdFromLog(log);
        if (!contactId) return;
        if (!logsByContactId[contactId]) logsByContactId[contactId] = [];
        logsByContactId[contactId].push(log);
    });

    const total = campaign?.contactCount || 0;
    const counts: CampaignStatusCounts = { entered: total, sending: 0, confirmed: 0, read: 0, failed: 0, total };

    for (const contactId of Object.keys(logsByContactId)) {
        const representativeLog = pickRepresentativeLog(logsByContactId[contactId]);
        if (!representativeLog || !representativeLog.status) continue;

        const { bucket, read } = classifyStatusCount(
            representativeLog.status,
            representativeLog.ack,
            representativeLog.message as UserMessage | undefined,
        );
        counts[bucket]++;
        if (bucket === 'confirmed' && read) counts.read++;
    }

    return counts;
}

export const selectCampaignStatusCounts = createDeepEqualSelector(
    [
        (state: CommonRootState, campaignId: string) => state.DBMessageLogReducer,
        (state: CommonRootState, campaignId: string) => state.DBCampaignReducer,
        (state: CommonRootState, campaignId: string) => campaignId
    ],
    (messageLogReducer, campaignReducer, campaignId): CampaignStatusCounts => {
        const campaign = campaignsSelectOneObjectByQuery(campaignReducer, {
            _id: campaignId,
        });
        const allLogs = messageLogSelectByQuery(messageLogReducer, {
            campaignId: campaignId
        });
        return computeCampaignStatusCounts(allLogs, campaign);
    }
);

// `chatId` is the contact grouping key (the concatenated contactList ids, i.e.
// getContactIdFromLog); `messageId` is retained only for callers that still pass
// it (no longer the grouping grain — see computeCampaignAxes).
type CampaignAxesLog = { messageId?: string; chatId?: string; status: string; ack?: number; message?: UserMessage };

/**
 * Pure two-axis reducer over raw message logs. Groups by CONTACT id (the same
 * grain as computeCampaignMessageStats / computeCampaignStatusCounts — the
 * concatenated contactList ids, carried on each row as `chatId`) so the two-axis
 * progress badge and the per-contact drip meter can never total differently for
 * the same campaign (a drip/retry where one contact has several messageIds no
 * longer over-counts here). Rows without a contact key are skipped, mirroring the
 * other two computes. Resolves each group's representative log with the shared
 * pickRepresentativeLog rule (most-advanced wire status; ties → higher ack; legacy
 * receipt-status rows unranked → a stale bogus 'played' row is harmless), then
 * projects wire via projectLogToAxes — which additionally folds an overdue/retry-
 * exhausted 'sent' message into terminal 'failed' (the old `expired` bucket) so a
 * completed campaign with a retry-exhausted send doesn't read "still sending"
 * forever. Non-overdue messages are untouched (approved fixture behavior).
 *
 * Receipt is resolved SEPARATELY from wire, aggregated across ALL rows of the
 * message (not just the representative row): legacy receipt info can live on a
 * row that isn't the most-advanced wire row (e.g. a sibling `status:'read'` row
 * next to the `confirmed` row that won the wire pick), so picking only the
 * representative row's ack drops it. maxReceipt folds each row's
 * logToAxes(...).receipt together and keeps the most-advanced one; group rows
 * still yield 'not_reported' (isGroup is constant per message).
 *
 * The two axes interact through one invariant: a message with real receipt
 * evidence (aggregatedReceipt 'delivered' or 'read') WAS delivered/read — it
 * reached the recipient, so it cannot be 'failed'/'queued'/expired. When
 * hasPositiveReceipt is true we therefore FORCE wire:'sent' directly, overriding
 * both projectLogToAxes's overdue→'failed' fold AND a genuinely terminal
 * representative status (e.g. a dup-send where the original was read but the
 * retry row is 'failed', which would otherwise win pickRepresentativeLog). This
 * guarantees two things at once: wire:'failed' and a real receipt can never
 * co-occur for the same message, AND a real receipt is never dropped by the
 * wire==='sent' receipt gate below. When there is NO positive receipt
 * (aggregatedReceipt 'none' or 'not_reported'), wire projects normally via
 * projectLogToAxes — pure-expired 1:1 AND group still fold to 'failed' (group
 * 'not_reported' is not positive delivery evidence, so it doesn't block the fold).
 */
export function computeCampaignAxes(logs: CampaignAxesLog[]) {
    // Group by contact id (the row's chatId == getContactIdFromLog, set by the
    // caller) — the SAME grain the per-contact stats use. Skip rows with no
    // contact key, exactly like computeCampaignMessageStats.
    const byContact = new Map<string, CampaignAxesLog[]>();
    for (const l of logs) {
        const key = l.chatId;
        if (!key) continue;
        const arr = byContact.get(key);
        if (arr) arr.push(l);
        else byContact.set(key, [l]);
    }
    const wire = { queued: 0, sent: 0, failed: 0 };
    const receipt = { delivered: 0, read: 0, notReported: 0 };
    for (const group of byContact.values()) {
        const rep = pickRepresentativeLog(group);
        if (!rep) continue;
        const isGroup = chatKindOf(rep.chatId) === 'group';

        // Receipt first — it decides whether wire is forced to 'sent'.
        const aggregatedReceipt = group.reduce<ReceiptState>(
            (acc, row) => maxReceipt(acc, logToAxes(row.status, row.ack, isGroup).receipt),
            'none'
        );
        const hasPositiveReceipt = aggregatedReceipt === 'delivered' || aggregatedReceipt === 'read';

        // Wire: positive receipt evidence FORCES 'sent' (a delivered/read message
        // reached the recipient — it can't be failed/expired, and its receipt must
        // not be dropped, even when the representative row is terminally 'failed').
        // With no positive receipt, project normally so pure-expired still folds to
        // 'failed' — for 1:1 AND group (not_reported doesn't count as positive).
        const wireState: WireState = hasPositiveReceipt
            ? 'sent'
            : projectLogToAxes(rep.status, rep.ack, isGroup, rep.message).wire;
        wire[wireState]++;

        // Receipt is only meaningful once the message actually reached the wire.
        // Gating on the FINAL wire state keeps the two axes consistent: a message
        // that folded to 'failed' (pure-expired, incl. an overdue group whose only
        // signal is not_reported) contributes NO receipt — never wire:'failed' +
        // a receipt bucket for the same message. positive-receipt messages always
        // have wire:'sent' here (forced above), so they still count exactly once.
        if (wireState === 'sent') {
            if (aggregatedReceipt === 'delivered') receipt.delivered++;
            else if (aggregatedReceipt === 'read') receipt.read++;
            else if (aggregatedReceipt === 'not_reported') receipt.notReported++;
        }
    }
    return { wire, receipt };
}

export interface CampaignAxesStats {
    wire: { queued: number; sent: number; failed: number };
    receipt: { delivered: number; read: number; notReported: number };
}

/**
 * Pure mapping MessageStats → CampaignProgressStats — the shape the progress BADGE
 * (multi-colour bar + "X/Y" counter, computeCampaignProgress) consumes.
 *
 * Keyed off the CURRENT MessageStats field names. This mapping regressed once already:
 * a rename read `messageStats['bt-sent']` and `messageStats.received` — neither of
 * which exists on MessageStats — so every field fed `undefined` into the sum and the
 * badge rendered "NaN/Y". Extracted + unit-tested here so a future rename can't
 * silently reintroduce that. Every read is `?? 0` guarded as belt-and-suspenders.
 *
 * `sending` = dispatched to WhatsApp (has a waMessageKey) — counts as sent. In the
 * two-axis model that is BOTH wire-reached buckets: `sent` (handed off, no server ack
 * yet) + `confirmed` (server-acked, no per-recipient receipt yet). `pending` = still
 * queued locally, not yet handed off — the unfilled remainder, NOT counted. Kept
 * separate so the bar's filled portion matches the counter (see computeCampaignProgress).
 */
export function messageStatsToProgressStats(messageStats?: MessageStats | null): CampaignProgressStats {
    if (!messageStats) {
        return { sending: 0, pending: 0, delivered: 0, read: 0, interacted: 0, failed: 0, expired: 0 };
    }
    return {
        sending: (messageStats.sent ?? 0) + (messageStats.confirmed ?? 0),
        pending: messageStats.pending ?? 0,
        delivered: messageStats.delivered ?? 0,
        read: messageStats.read ?? 0,
        interacted: messageStats.played ?? 0,
        failed: messageStats.failed ?? 0,
        expired: messageStats.expired ?? 0,
    };
}

export const selectCampaignProgressStats = createSelector(
    [
        (state: CommonRootState, campaignId: string) => selectCampaignMessageStats(state, campaignId)
    ],
    (messageStats): CampaignProgressStats => messageStatsToProgressStats(messageStats)
);

/**
 * THE classifier behind bulk-logs-view's status-filter tabs. Reclassifies a
 * representative log off the canonical two-axis reader (deriveMessageStatus →
 * {primary, receipt}) so the filtered list agrees with the stats panel
 * (computeCampaignMessageStats), which is built off the SAME reader. Retires the
 * legacy `ackData.{delivery,read,played}Remaining` blob these filters used to
 * read — that blob is incompatible with the ack-integer path the stats use.
 *
 * Tab → axes mapping (mirrors the stats-panel buckets):
 *   - 'failed'    → primary 'failed' (failed/error/cancelled/expired — NOT just
 *                   the old status==='error'), so the failed tab and the failed
 *                   stat count the same rows.
 *   - 'delivered' → receipt 'delivered'.
 *   - 'read'      → receipt 'read' (the reader folds ack 'played' into 'read').
 *   - 'played'    → NEVER matches: the canonical receipt axis has no 'played'
 *                   state (mirrors computeCampaignMessageStats, where `played` is
 *                   always 0), so the tab is empty — consistent with its 0 count.
 *   - 'pending'   → primary 'queued'.
 *   - 'sent'      → "Sending": reached the wire/server but no per-recipient
 *                   delivery receipt yet — primary 'sent' OR 'confirmed', receipt
 *                   not delivered/read (matches the panel's sent + confirmed).
 */
export function logMatchesCampaignFilter(
    log: { status?: string; ack?: number; message?: { contactList?: { id?: string }[] } },
    activeFilter: string | null | undefined,
    // BE#658: live store userMessage for the delivery-evidence override — so a delivered send
    // (confirmed + keyed) does NOT match the "Failed"/"pending" tabs and DOES match "Sent",
    // even on a stuck/stray log trail. Falls back to the log's embedded snapshot.
    deliveryMessage?: UserMessage | null,
): boolean {
    if (!activeFilter) return true;
    if (!log.status) return false;

    const isGroup = chatKindOf(getContactIdFromLog(log)) === 'group';
    const { primary: rawPrimary, receipt } = deriveMessageStatus(log.status, log.ack, isGroup);
    const primary =
        (rawPrimary === 'failed' || rawPrimary === 'queued')
        && messageHasDeliveryEvidence(deliveryMessage ?? (log.message as UserMessage | undefined))
            ? 'confirmed'
            : rawPrimary;

    switch (activeFilter) {
        case 'failed':
            return primary === 'failed';
        case 'not_on_whatsapp':
            // BE#710 — the "View these numbers" banner filter: failed recipients whose
            // failure is specifically "not on WhatsApp" (curated code or legacy raw text).
            return primary === 'failed'
                && isNotOnWhatsAppFailure((deliveryMessage ?? (log.message as UserMessage | undefined)) ?? null);
        case 'delivered':
            return receipt === 'delivered';
        case 'read':
            return receipt === 'read';
        case 'played':
            return false; // no 'played' in the canonical receipt axis (folds to read)
        case 'pending':
            return primary === 'queued';
        case 'sent':
            return (primary === 'sent' || primary === 'confirmed')
                && receipt !== 'delivered' && receipt !== 'read';
        default:
            return true;
    }
}

export const selectFilteredCampaignLogs = createDeepEqualSelector(
    [
        (state: CommonRootState, campaignId: string) => selectUniqueMessageLogsByCampaign(state, campaignId),
        (state: CommonRootState, campaignId: string, filter: string | null) => filter,
        (state: CommonRootState) => state.DBUserMessageReducer,
        (state: CommonRootState) => state.CampaignReducer.deliveryEvidence,
    ],
    (uniqueLogs, activeFilter, userMessageReducer, deliveryEvidence) => {
        const filteredLogs = Object.values(uniqueLogs);
        if (!activeFilter) return filteredLogs;
        return filteredLogs.filter(messageLog => logMatchesCampaignFilter(
            messageLog,
            activeFilter,
            messageLog.messageId
                ? ((userMessagesSelectOneObjectByQuery(userMessageReducer, { _id: messageLog.messageId }) as UserMessage | undefined)
                    ?? (deliveryEvidence?.[messageLog.messageId] as UserMessage | undefined))
                : undefined,
        ));
    }
);

// Bulk Dashboard UI State Selectors
export const selectBulkLogsViewActiveTab = createSelector(
    [(state: CommonRootState) => state.CampaignReducer],
    (campaignReducer) => campaignReducer.bulkLogsViewActiveTab
);

export const selectBulkLogsViewActiveFilter = createSelector(
    [(state: CommonRootState) => state.CampaignReducer],
    (campaignReducer) => campaignReducer.bulkLogsViewActiveFilter
);

export const selectBulkCampaignDialogSearchTerm = createSelector(
    [(state: CommonRootState) => state.CampaignReducer],
    (campaignReducer) => campaignReducer.bulkCampaignDialogSearchTerm
);

export const selectBulkAudiencesListSearchTerm = createSelector(
    [(state: CommonRootState) => state.CampaignReducer],
    (campaignReducer) => campaignReducer.bulkAudiencesListSearchTerm
);

export const selectBulkTemplateDialogSearchTerm = createSelector(
    [(state: CommonRootState) => state.CampaignReducer],
    (campaignReducer) => campaignReducer.bulkTemplateDialogSearchTerm
);

// Derived selectors for filtered data
export const selectFilteredCampaignLogsByFilter = createDeepEqualSelector(
    [
        (state: CommonRootState, campaignId: string) => selectUniqueMessageLogsByCampaign(state, campaignId),
        (state: CommonRootState) => state.CampaignReducer.bulkLogsViewActiveFilter,
        (state: CommonRootState) => state.DBUserMessageReducer,
        (state: CommonRootState) => state.CampaignReducer.deliveryEvidence,
    ],
    (uniqueLogs, activeFilter, userMessageReducer, deliveryEvidence) => {
        const filteredLogs = Object.values(uniqueLogs);
        if (!activeFilter) return filteredLogs;
        return filteredLogs.filter(messageLog => logMatchesCampaignFilter(
            messageLog,
            activeFilter,
            messageLog.messageId
                ? ((userMessagesSelectOneObjectByQuery(userMessageReducer, { _id: messageLog.messageId }) as UserMessage | undefined)
                    ?? (deliveryEvidence?.[messageLog.messageId] as UserMessage | undefined))
                : undefined,
        ));
    }
);

export const selectFilteredCampaignsBySearch = createSelector(
    [
        (state: CommonRootState) => state.DBCampaignReducer,
        selectBulkCampaignDialogSearchTerm
    ],
    (campaignReducer, searchTerm) => {
        if (!searchTerm) return [];
        
        const campaigns = campaignsSelectByQuery(campaignReducer, {
            $or: [
                { 'template.name': { $regex: searchTerm, $options: 'i' } },
                { 'template.message': { $regex: searchTerm, $options: 'i' } },
                { 'template.poll.question': { $regex: searchTerm, $options: 'i' } },
            ],
        }, [], { updatedAt: -1 });
        
        return campaigns;
    }
);

export const selectFilteredAudiencesBySearch = createSelector(
    [
        (state: CommonRootState) => state.DBBoardsReducer,
        selectBulkAudiencesListSearchTerm
    ],
    (boardsReducer, searchTerm) => {
        if (!searchTerm) return [];
        
        const boards = boardsSelectByQuery(boardsReducer, {
            'name': { $regex: searchTerm, $options: 'i' },
            type: 'audience',
        }, [], { updatedAt: -1 });
        
        return boards;
    }
);

/**
 * Selector to get the most advanced status achieved from all message logs for a messageId
 * Returns the status with the lowest level number (most advanced)
 */
export const selectMostAdvancedMessageLogStatus = createSelector(
    [
        (state: CommonRootState) => state.DBMessageLogReducer,
        (_: CommonRootState, messageId: string | undefined) => messageId
    ],
    (messageLogReducer, messageId) => {
        if (!messageId) return null;
        
        // Get all message logs for this messageId
        const logs = messageLogSelectByQuery(messageLogReducer, {
            messageId: messageId
        });
        
        if (!logs || logs.length === 0) return null;

        // Most-advanced = the representative row under the shared statusRank rule
        // (ties broken by higher ack). One resolution rule, shared with the
        // aggregate stats, so the row badge and the header can never disagree.
        return pickRepresentativeLog(logs)?.status ?? null;
    }
);

/**
 * Selector to determine if message is confirmed or beyond.
 * "Confirmed or beyond" = the canonical reader folds the representative status to
 * the `confirmed` primary (status 'confirmed', OR a legacy receipt status
 * delivered/received/read/played). A message in that state has reached WhatsApp's
 * servers and must not be retried — so the retry selectors below are gated on the
 * negation. NOTE: this now also covers a `delivered` message, which the old
 * `level <= 1.5` threshold wrongly excluded (would have offered retry on it).
 */
// BE#658: the message that carries delivery evidence for this messageId — the userMessage
// from the store, falling back to the representative log's embedded message snapshot (the same
// two sources the per-message badge reads). Lets selectIsConfirmedOrBeyond honour a delivered
// send over a stray `failed` log trail, so Retry is hidden on an already-delivered message.
const selectDeliveryEvidenceMessage = (
    state: CommonRootState,
    messageId: string | undefined,
): UserMessage | null => {
    if (!messageId) return null;
    const um = userMessagesSelectOneObjectByQuery(state.DBUserMessageReducer, { _id: messageId });
    if (um) return um as UserMessage;
    // BE#658 (4a): dedicated delivery-evidence map (scoped fetch for the log view) before the
    // pre-send embedded snapshot, so Retry is correctly hidden on a delivered before-wa-send-only row.
    const evidence = state.CampaignReducer.deliveryEvidence?.[messageId];
    if (evidence) return evidence as UserMessage;
    const logs = messageLogSelectByQuery(state.DBMessageLogReducer, { messageId });
    return (pickRepresentativeLog(logs)?.message as UserMessage | undefined) ?? null;
};

export const selectIsConfirmedOrBeyond = createSelector(
    [selectMostAdvancedMessageLogStatus, selectDeliveryEvidenceMessage],
    (mostAdvancedStatus, deliveryEvidenceMessage) =>
        isConfirmedOrBeyondFrom(mostAdvancedStatus, deliveryEvidenceMessage),
);

/**
 * Selector to determine if retry button should be shown
 */
export const selectShouldShowRetry = createSelector(
    [
        selectIsConfirmedOrBeyond,
        (_: CommonRootState, messageId: string | undefined) => messageId
    ],
    (isConfirmedOrBeyond, messageId) => {
        return !isConfirmedOrBeyond && !!messageId;
    }
);

/**
 * BE#710 (conductor ruling) — should a terminal failure offer a Retry (per-row Retry AND
 * "Retry All")? A not-on-WhatsApp failure is terminal — the recipient number isn't registered on
 * WhatsApp, so a resend can never succeed — and is therefore excluded. Pure; shared by
 * selectMessagesToRetry (the Retry-All count + handler) and the campaign log row's Retry control
 * so the two never diverge.
 */
export function isRetryableLogMessage(
    baseShouldRetry: boolean,
    message: UserMessage | null | undefined,
): boolean {
    if (!baseShouldRetry) return false;
    return !isNotOnWhatsAppFailure(message ?? null);
}

/**
 * Selector to get messages that should be retried
 * Returns filtered logs where selectShouldShowRetry returns true AND the failure is retryable
 * (BE#710: not-on-WhatsApp failures are terminal and excluded). Uses selectShouldShowRetry +
 * isRetryableLogMessage so the per-row control and "Retry All" share one decision.
 */
export const selectMessagesToRetry = createSelector(
    [
        (state: CommonRootState, campaignId: string) => selectFilteredCampaignLogsByFilter(state, campaignId),
        (state: CommonRootState) => state
    ],
    (filteredLogs, state) => {
        // Filter messages that should show retry using the same selector logic
        return filteredLogs.filter(log => {
            const messageId = log?.messageId;
            if (!messageId) return false;
            const baseShouldRetry = selectShouldShowRetry(state, messageId);
            // BE#710: resolve the message the same way the status filter does — full store doc →
            // delivery-evidence (carries the failure reason after BE#710) → the log's embedded
            // snapshot — so a not-on-WhatsApp failure is recognised and excluded from Retry All.
            const resolved = (userMessagesSelectOneObjectByQuery(state.DBUserMessageReducer, { _id: messageId }) as UserMessage | undefined)
                ?? (state.CampaignReducer.deliveryEvidence?.[messageId] as UserMessage | undefined)
                ?? (log.message as UserMessage | undefined);
            return isRetryableLogMessage(baseShouldRetry, resolved);
        });
    }
);

/**
 * Selector to determine if "Retry All" button should be shown
 * Returns true if any message in the filtered logs should show a retry button
 * Uses selectShouldShowRetry internally to reuse the same logic
 */
export const selectShouldShowRetryAll = createSelector(
    [selectMessagesToRetry],
    (messagesToRetry) => {
        return messagesToRetry.length > 0;
    }
);

export const selectCampaignsWithUndeliveredMessages = createSelector(
    [
        (state: CommonRootState) => state,
        getRunningCampaigns
    ],
    (state: CommonRootState, inProgressCampaigns): Campaign[] => {
        return inProgressCampaigns.filter(campaign => {
            const progressStats = selectCampaignProgressStats(state, campaign._id ?? '');
            const totalContacts = campaign?.contactCount || campaign?.list?.contactIdList?.length || 0;

            if (totalContacts === 0) return false;

            // A campaign is still "in progress" only until every message has been processed (sent counts
            // as processed — same rule the card uses). Once all-processed it drops out of the running set,
            // even if its backend campaignStatus hasn't flipped away from 'running' yet.
            return !computeCampaignProgress(progressStats, totalContacts).isAllProcessed;
        });
    }
);

/**
 * The set backing the "Running Now" tab. `campaignStatus: 'running'` filtered by actual progress, so a
 * campaign whose messages are all processed (all sent) leaves the tab immediately — matching the card's
 * Completed badge — instead of lingering until the backend flips its status.
 */
export const selectRunningNotCompletedCampaigns = createSelector(
    [
        (state: CommonRootState) => state.DBCampaignReducer,
        (state: CommonRootState) => state,
    ],
    (campaignReducer, state): Campaign[] => {
        const running = campaignsSelectByQuery(campaignReducer, { campaignStatus: 'running' }, [], { 'schedule.dueDate': -1 });
        return running.filter(campaign => {
            const totalContacts = campaign?.contactCount || campaign?.list?.contactIdList?.length || 0;
            if (totalContacts === 0) return true; // no contacts resolved yet → still counts as running
            const progressStats = selectCampaignProgressStats(state, campaign._id ?? '');
            return !computeCampaignProgress(progressStats, totalContacts).isAllProcessed;
        });
    }
);

export const selectActiveCampaignId = createSelector(
    [
        getRunningCampaigns,
        (_state: CommonRootState, campaignId?: string) => campaignId
    ],
    (runningCampaigns, campaignId) => {
        if (campaignId) return campaignId;
        return runningCampaigns?.[0]?._id;
    }
);
